/**
 * NuGuard API Client
 * 
 * HTTP client with JWT authentication, automatic token refresh,
 * and Zod schema validation for API responses.
 */

import { z } from 'zod';
import { CONFIG } from '../config';
import { getTracingHeaders } from './posthog';
import {
  LoginRequestSchema,
  TokenResponseSchema,
  UserSchema,
  ApplicationSchema,
  ApplicationCreateSchema,
  PolicySchema,
  PolicyCreateSchema,
  PolicyUpdateSchema,
  PolicyControlSchema,
  ControlSchema,
  ScanResponseSchema,
  ScanListItemSchema,
  AIAssetSchema,
  AuditLogSchema,
  // AIBOM Schemas
  AIBOMScanResponseSchema,
  AIBOMScanListResponseSchema,
  AIBOMScanSummaryResponseSchema,
  AIBOMNodeResponseSchema,
  AIBOMNodeListResponseSchema,
  AIBOMEdgeListResponseSchema,
  AIBOMEvidenceListResponseSchema,
  AIBOMGraphResponseSchema,
  NodeTypeCountSchema,
  ScanDependenciesResponseSchema,
  // Assessment Schemas
  AssessmentResponseSchema,
  AssessmentListResponseSchema,
  AssessmentResultResponseSchema,
  ControlListResponseSchema,
  OrganizationAttestationResponseSchema,
  type LoginRequest,
  type TokenResponse,
  type User,
  type UserCreate,
  type UserUpdate,
  type Application,
  type ApplicationCreate,
  type Policy,
  type PolicyCreate,
  type PolicyUpdate,
  type PolicyControl,
  type Control,
  type ScanResponse,
  type ScanListItem,
  type ScanStatusResponse,
  type AIAsset,
  type AuditLog,
  // AIBOM Types
  type AIBOMScanCreate,
  type AIBOMScanResponse,
  type AIBOMScanListResponse,
  type AIBOMScanSummaryResponse,
  type AIBOMNodeResponse,
  type AIBOMNodeListResponse,
  type AIBOMEdgeListResponse,
  type AIBOMEvidenceListResponse,
  type AIBOMGraphResponse,
  type NodeTypeCount,
  type ScanDependenciesResponse,
  // Assessment Types
  type AssessmentCreate,
  type AssessmentResponse,
  type AssessmentListResponse,
  type AssessmentResultResponse,
  type ControlListResponse,
  type OrganizationAttestationResponse,
  type ReportStatus,
  // Risk Catalog (phase 1)
  RiskCatalogListResponseSchema,
  RiskCatalogRecordDetailSchema,
  type RiskCatalogListResponse,
  type RiskCatalogRecordDetail,
} from '../schemas';
import {
  SubscriptionPlanSchema,
  TenantSubscriptionSchema,
  CreditTransactionSchema,
  UsageSummarySchema,
  UsageByDimensionSchema,
  QuotaStatusSchema,
  LicensingNotificationSchema,
} from '../types';
import type {
  SubscriptionPlan,
  TenantSubscription,
  CreditTransaction,
  UsageSummary,
  UsageByDimension,
  QuotaStatus,
  LicensingNotification,
} from '../types';

// =============================================================================
// Constants
// =============================================================================

const DATA_SERVICE_URL = CONFIG.DATA_SERVICE_URL || '/api/data';
const ASSETS_SERVICE_URL = CONFIG.ASSETS_SERVICE_URL || '/api/assets';
const COMPLIANCE_SERVICE_URL = CONFIG.COMPLIANCE_SERVICE_URL || '/api/compliance';
const CHAT_SERVICE_URL = CONFIG.CHAT_SERVICE_URL || '/api/chat';
const REDTEAM_SERVICE_URL = CONFIG.REDTEAM_SERVICE_URL || '/api/redteam';
const TOKEN_STORAGE_KEY = 'nuguard_access_token';
const REFRESH_TOKEN_KEY = 'nuguard_refresh_token';
const TOKEN_EXPIRY_KEY = 'nuguard_token_expiry';
const SESSION_MARKER_KEY = 'nuguard_session_active';
// Identifies which login this tab belongs to. sessionStorage (and thus this
// key) is copied wholesale when a tab is duplicated, so duplicated tabs share
// their parent's session id; a tab that logs in independently gets its own.
// Used to scope cross-tab logout sync (see AUTH_BROADCAST_CHANNEL below) so
// logging out a duplicated tab doesn't affect unrelated, independently
// logged-in tabs.
const SESSION_ID_KEY = 'nuguard_session_id';
const AUTH_BROADCAST_CHANNEL = 'nuguard-auth';
// Short-lived localStorage bridge so a new tab opened via window.open() can
// inherit the session without re-login. Consumed once on load then deleted.
const TOKEN_BRIDGE_KEY = 'nuguard_token_bridge';
const TOKEN_BRIDGE_TTL_MS = 10_000; // 10 s should be plenty for tab open

/**
 * Write current tokens to the one-shot localStorage bridge.
 * Call this immediately before window.open() for a new authenticated tab.
 */
export function writeTokenBridge(): void {
  const at = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  const rt = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  const exp = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!at || !rt) return;
  localStorage.setItem(TOKEN_BRIDGE_KEY, JSON.stringify({
    accessToken: at,
    refreshToken: rt,
    expiry: exp,
    created: Date.now(),
  }));
}

// Refresh token 2 minutes before expiry for seamless UX
const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;
// Refresh is a small POST — fail fast instead of hanging on the browser's default TCP timeout
const REFRESH_TIMEOUT_MS = 15 * 1000;
const DEBUG_FRONTEND_LOGS = CONFIG.DEBUG_FRONTEND_LOGS;

const safeChatActionPath = (path?: string): string | null => {
  const normalized = (path || '').trim();
  if (normalized === '/chat/tests/run-redteam' || normalized === '/tests/run-redteam') {
    // Always return without leading /chat/ to avoid double-prefix when
    // concatenated with CHAT_SERVICE_URL (which is already /api/chat).
    return '/tests/run-redteam';
  }
  return null;
};

const debugLog = (message: string, payload?: Record<string, unknown>): void => {
  if (!DEBUG_FRONTEND_LOGS) return;
  if (payload) {
    console.info(message, payload);
    return;
  }
  console.info(message);
};

// =============================================================================
// Token Management
// =============================================================================

class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private authChannel: BroadcastChannel | null = null;

  constructor() {
    // Token storage strategy: use sessionStorage instead of localStorage
    // to limit XSS exposure. sessionStorage is cleared when the tab closes,
    // so tokens can't be exfiltrated from a persistent localStorage store.
    const isExistingSession = sessionStorage.getItem(SESSION_MARKER_KEY);

    debugLog('[auth/token] Initializing token manager', {
      hasSessionMarker: Boolean(isExistingSession),
    });

    // Consume one-shot bridge token written by the parent tab before window.open().
    // Must run before the session-marker check so the new tab is treated as authenticated.
    const rawBridge = localStorage.getItem(TOKEN_BRIDGE_KEY);
    if (rawBridge) {
      localStorage.removeItem(TOKEN_BRIDGE_KEY);
      try {
        const bridge = JSON.parse(rawBridge) as {
          accessToken: string; refreshToken: string;
          expiry: string | null; created: number;
        };
        if (
          bridge.accessToken && bridge.refreshToken &&
          Date.now() - bridge.created < TOKEN_BRIDGE_TTL_MS
        ) {
          sessionStorage.setItem(TOKEN_STORAGE_KEY, bridge.accessToken);
          sessionStorage.setItem(REFRESH_TOKEN_KEY, bridge.refreshToken);
          if (bridge.expiry) sessionStorage.setItem(TOKEN_EXPIRY_KEY, bridge.expiry);
          sessionStorage.setItem(SESSION_MARKER_KEY, 'true');
          debugLog('[auth/token] Hydrated session from bridge token');
        }
      } catch {
        // malformed bridge — ignore, fall through to normal init
      }
    }

    // Re-read SESSION_MARKER_KEY after the bridge code may have set it.
    // Using the pre-bridge snapshot (`isExistingSession`) would incorrectly
    // clear tokens that the bridge just wrote into sessionStorage.
    if (!sessionStorage.getItem(SESSION_MARKER_KEY)) {
      // Fresh browser session - clear any stale tokens
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
      // Also clear legacy localStorage tokens from before this change
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      sessionStorage.setItem(SESSION_MARKER_KEY, 'true');
    }

    // Load tokens from sessionStorage (scoped to this tab only)
    this.accessToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    this.refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    const storedExpiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    this.tokenExpiry = storedExpiry ? parseInt(storedExpiry, 10) : null;

    debugLog('[auth/token] Loaded token state', {
      hasAccessToken: Boolean(this.accessToken),
      hasRefreshToken: Boolean(this.refreshToken),
      hasExpiry: Boolean(this.tokenExpiry),
    });

    // Schedule proactive refresh if we have valid tokens
    this.scheduleProactiveRefresh();

    this.initAuthChannel();
  }

  /**
   * Cross-tab logout sync. Duplicated tabs inherit the parent's sessionStorage
   * (including SESSION_ID_KEY) so they share a session id and stay in sync;
   * a tab that logs in independently mints its own id in setTokens() and is
   * ignored by broadcasts from unrelated sessions.
   */
  private initAuthChannel(): void {
    if (this.authChannel || typeof BroadcastChannel === 'undefined') return;
    this.authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    this.authChannel.onmessage = (event: MessageEvent<{ type: string; sessionId: string }>) => {
      if (event.data?.type !== 'logout') return;
      const ownSessionId = sessionStorage.getItem(SESSION_ID_KEY);
      if (!ownSessionId || event.data.sessionId !== ownSessionId) return;
      debugLog('[auth/token] Received cross-tab logout for this session');
      this.clearTokens({ broadcast: false });
      if (!sessionExpiredRedirectInFlight) {
        sessionExpiredRedirectInFlight = true;
        window.location.href = '/';
      }
    };
  }

  setTokens(accessToken: string, refreshToken: string, expiresInSeconds?: number): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // Mint a fresh session id on every real login. A tab duplicated after this
    // point inherits it via copied sessionStorage; a tab that logs in on its
    // own always gets a new one here.
    sessionStorage.setItem(SESSION_ID_KEY, crypto.randomUUID());

    // Calculate and store expiry time
    if (expiresInSeconds) {
      this.tokenExpiry = Date.now() + (expiresInSeconds * 1000);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry.toString());
    }

    // Schedule proactive refresh
    this.scheduleProactiveRefresh();
    this.initAuthChannel();
  }

  clearTokens(options: { broadcast?: boolean } = {}): void {
    const { broadcast = true } = options;
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);

    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);

    // Clear any pending refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (broadcast && sessionId) {
      this.authChannel?.postMessage({ type: 'logout', sessionId });
    }

    debugLog('[auth/token] Cleared all tokens');
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }
  
  /**
   * Check if token is about to expire (within buffer period)
   */
  isTokenExpiringSoon(): boolean {
    if (!this.tokenExpiry) return false;
    return Date.now() >= (this.tokenExpiry - TOKEN_REFRESH_BUFFER_MS);
  }
  
  /**
   * Schedule automatic token refresh before expiry
   */
  private scheduleProactiveRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    if (!this.tokenExpiry || !this.refreshToken) return;
    
    // Calculate when to refresh (2 minutes before expiry)
    const refreshAt = this.tokenExpiry - TOKEN_REFRESH_BUFFER_MS;
    const delay = refreshAt - Date.now();
    
    if (delay <= 0) {
      // Token is already expired or expiring soon - refresh immediately
      this.refreshAccessToken().catch(() => {
        // Silent fail - will be handled on next API call
      });
    } else {
      // Schedule refresh for later
      this.refreshTimer = setTimeout(() => {
        this.refreshAccessToken().catch(() => {
          // Silent fail - will be handled on next API call
        });
      }, delay);
    }
  }

  /**
   * Refresh the access token using the refresh token.
   * Uses a promise lock to prevent multiple simultaneous refresh requests.
   */
  async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    debugLog('[auth/token] Refreshing access token');
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${DATA_SERVICE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
          signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
        });

        if (!response.ok) {
          // Only 401/403 means the refresh token itself was rejected —
          // that's a real "you must log in again". Any other status (5xx,
          // or the service being briefly unreachable mid-restart) is a
          // transient failure: keep the existing tokens so the next retry
          // can still succeed instead of force-logging-out an active session.
          if (response.status === 401 || response.status === 403) {
            this.clearTokens();
          }
          throw new Error('Token refresh failed');
        }

        const data = TokenResponseSchema.parse(await response.json());
        this.accessToken = data.access_token;
        sessionStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);

        // Update expiry time if provided
        if (data.expires_in) {
          this.tokenExpiry = Date.now() + (data.expires_in * 1000);
          sessionStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry.toString());
        }
        
        // Schedule next proactive refresh
        this.scheduleProactiveRefresh();
        debugLog('[auth/token] Token refresh successful', {
          hasAccessToken: Boolean(data.access_token),
          expiresIn: data.expires_in ?? null,
        });

      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }
}

export const tokenManager = new TokenManager();

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly detail: string;
  /** Raw parsed JSON body from the error response, if available. */
  readonly data: unknown;

  constructor(status: number, url: string, detail: string, fallbackMessage?: string, data?: unknown) {
    super(detail || fallbackMessage || `API Error: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.detail = detail;
    this.data = data;
  }
}

// Guards against multiple concurrent requests each independently redirecting
// the page when a session expires — only the first one should navigate away;
// the rest just need to reject so their callers stop waiting on stale data.
let sessionExpiredRedirectInFlight = false;

function triggerSessionExpiredRedirect(url: string): never {
  tokenManager.clearTokens();
  if (!sessionExpiredRedirectInFlight) {
    sessionExpiredRedirectInFlight = true;
    window.location.href = '/';
  }
  throw new ApiError(401, url, '', 'Your session has expired. Please log in again to continue.');
}

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status;

  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      status?: number | string;
      statusCode?: number | string;
      response?: { status?: number | string };
    };

    const directStatus = maybeError.status ?? maybeError.statusCode;
    if (typeof directStatus === 'number') return directStatus;
    if (typeof directStatus === 'string') {
      const parsed = Number.parseInt(directStatus, 10);
      if (Number.isFinite(parsed)) return parsed;
    }

    const responseStatus = maybeError.response?.status;
    if (typeof responseStatus === 'number') return responseStatus;
    if (typeof responseStatus === 'string') {
      const parsed = Number.parseInt(responseStatus, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return undefined;
}

// =============================================================================
// API Client
// =============================================================================

/** Pause for a given number of milliseconds. */
function _sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

class ApiClient {
  /** In-flight GET request deduplication map. */
  private _inflight = new Map<string, Promise<unknown>>();

  /** Default request timeout in milliseconds. */
  private _timeoutMs: number;

  /**
   * Maximum number of automatic retries for cold-start errors (502/503 and
   * network-level failures). Azure Container Apps scale-to-zero means the
   * first request after a period of inactivity may hit a container that is
   * still spinning up, returning 502 until ready.
   */
  private _maxRetries: number;

  constructor(timeoutMs = 60_000, maxRetries = 3) {
    this._timeoutMs = timeoutMs;
    this._maxRetries = maxRetries;
  }

  /**
   * Make an authenticated HTTP request with automatic token refresh.
   *
   * Supports:
   * - Request deduplication for GET requests (same URL returns same promise)
   * - AbortSignal for cancellation on component unmount
   * - Configurable timeout (default 60s)
   * - Automatic retry with exponential back-off for 502/503 and network errors
   *
   * Uses z.ZodTypeAny for schema parameter to allow schemas with transforms,
   * defaults, and catches. The return type is inferred from the explicit
   * type parameter T, and the schema validates the data at runtime.
   */
  private async request<T>(
    url: string,
    options: RequestInit = {},
    schema?: z.ZodTypeAny
  ): Promise<T> {
    const method = options.method || 'GET';

    // Dedup GET requests: if same URL is already in flight, return existing promise
    if (method === 'GET') {
      const existing = this._inflight.get(url);
      if (existing) {
        return existing as Promise<T>;
      }
    }

    const promise = this._executeRequest<T>(url, options, schema);

    // Track GET requests for dedup, remove when complete.
    // Swallow rejections on this derived promise — it exists only for cache
    // cleanup; the original `promise` (returned below) still rejects normally
    // for the real caller, this just avoids leaving an unhandled rejection
    // dangling on every failed GET request.
    if (method === 'GET') {
      this._inflight.set(url, promise);
      promise.finally(() => this._inflight.delete(url)).catch(() => {});
    }

    return promise;
  }

  private async _executeRequest<T>(
    url: string,
    options: RequestInit = {},
    schema?: z.ZodTypeAny,
    attempt = 1
  ): Promise<T> {
    const method = options.method || 'GET';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getTracingHeaders(),
      ...(options.headers as Record<string, string>),
    };

    // Proactive token refresh: if token is expiring soon, refresh before making request
    // This handles edge cases like laptop sleep where timers don't fire
    if (tokenManager.isTokenExpiringSoon() && tokenManager.getRefreshToken()) {
      try {
        await tokenManager.refreshAccessToken();
      } catch {
        // Refresh failed - continue with current token, will be handled by 401 logic below
      }
    }

    // Add authorization header if authenticated
    const token = tokenManager.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    debugLog('[apiClient] Request start', {
      method,
      url,
      hasAuthHeader: Boolean(headers['Authorization']),
    });

    let response: Response;
    try {
      // Combine caller signal with timeout signal
      const timeoutSignal = AbortSignal.timeout(this._timeoutMs);
      const signals = [timeoutSignal];
      if (options.signal) signals.push(options.signal);
      const combinedSignal = signals.length === 1
        ? signals[0]
        : AbortSignal.any(signals);

      response = await fetch(url, { ...options, headers, signal: combinedSignal });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Request was cancelled.');
      }
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new Error('Request timed out. Please try again.');
      }
      // Network-level failure (e.g. container not yet accepting connections during
      // a cold start). Retry with exponential back-off before giving up.
      if (attempt <= this._maxRetries) {
        const delay = Math.min(1_000 * attempt, 8_000);
        console.warn(`[apiClient] Network error on attempt ${attempt}/${this._maxRetries} — ${method} ${url} — retrying in ${delay}ms`, error);
        await _sleep(delay);
        return this._executeRequest(url, options, schema, attempt + 1);
      }
      console.error(`[apiClient] Network error — ${method} ${url}`, error);
      throw new Error('Connection failed. Please check if the NuGuard backend is running and reachable.');
    }

    debugLog('[apiClient] Response received', {
      method,
      url,
      status: response.status,
      ok: response.ok,
    });

    // Handle 401 for authenticated requests only:
    // login/refresh and other anonymous calls should surface backend error details directly.
    if (response.status === 401 && Boolean(token)) {
      if (tokenManager.getRefreshToken()) {
        try {
          await tokenManager.refreshAccessToken();
          headers['Authorization'] = `Bearer ${tokenManager.getAccessToken()}`;
          response = await fetch(url, { ...options, headers });
          
          // If still 401 after refresh, logout
          if (response.status === 401) {
            console.warn('Still 401 after token refresh, logging out');
            triggerSessionExpiredRedirect(url);
          }
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          console.warn('Token refresh failed:', refreshError);
          triggerSessionExpiredRedirect(url);
        }
      } else {
        // No refresh token available - redirect to login
        console.warn('No refresh token, logging out');
        triggerSessionExpiredRedirect(url);
      }
    }

    if (!response.ok) {
      debugLog('[apiClient] Response not ok', {
        method,
        url,
        status: response.status,
      });
      // Parse the error response body.  The backend can return either:
      //   { "detail": "some string" }                 — standard FastAPI error
      //   { "detail": { "code": "...", "message": "...", ...} }  — structured error
      // We extract a human-readable string and also preserve the raw object so
      // callers (e.g. PolicyCenter) can read structured fields like existing_id.
      let errorDetail = '';
      let rawErrorBody: unknown;
      try {
        const errorBody = await response.text();
        rawErrorBody = JSON.parse(errorBody);
        const body = rawErrorBody as Record<string, unknown>;
        if (typeof body?.detail === 'string') {
          errorDetail = body.detail;
        } else if (body?.detail && typeof (body.detail as Record<string, unknown>)?.message === 'string') {
          // Structured detail object — extract the human-readable message field
          errorDetail = (body.detail as Record<string, unknown>).message as string;
        }
      } catch {
        // Couldn't parse error body — errorDetail stays ''
      }

      if (response.status === 404) {
        throw new ApiError(response.status, url, errorDetail, `The requested resource was not found: ${url}`, rawErrorBody);
      }

      if (response.status === 422) {
        throw new ApiError(response.status, url, errorDetail, 'Data validation failed. Please check your input and try again.', rawErrorBody);
      }

      if (response.status === 429) {
        throw new ApiError(response.status, url, errorDetail, 'Rate limit exceeded. Please wait a moment and try again.', rawErrorBody);
      }

      if (response.status === 502 || response.status === 503) {
        // Container may be cold-starting (Azure Container Apps scale-to-zero).
        // Retry with exponential back-off before surfacing the error.
        if (attempt <= this._maxRetries) {
          const delay = Math.min(2_000 * attempt, 10_000);
          console.warn(`[apiClient] ${response.status} on attempt ${attempt}/${this._maxRetries} — ${method} ${url} — retrying in ${delay}ms`);
          await _sleep(delay);
          return this._executeRequest(url, options, schema, attempt + 1);
        }
        throw new ApiError(
          response.status,
          url,
          errorDetail,
          'The service is temporarily unavailable (it may be starting up). Please try again in a moment.',
          rawErrorBody
        );
      }

      if (response.status >= 500) {
        throw new ApiError(
          response.status,
          url,
          errorDetail,
          'An internal server error occurred. Our team has been notified.',
          rawErrorBody
        );
      }

      throw new ApiError(response.status, url, errorDetail, undefined, rawErrorBody);
    }

    const data = await response.json();

    // Validate response with Zod schema if provided
    if (schema) {
      try {
        const validated = schema.parse(data);
        console.log('[apiClient] Validation successful for', url);
        return validated;
      } catch (error) {
        console.error('[apiClient] Response validation failed for', url);
        console.error('[apiClient] Raw data:', JSON.stringify(data, null, 2).substring(0, 1000));
        console.error('[apiClient] Validation error:', error);
        // Surface a descriptive message so hooks/components can show it to the user
        const endpoint = url.split('?')[0].split('/').slice(-2).join('/');
        throw new ApiError(
          0,
          url,
          `Data validation failed for ${endpoint}. Please try refreshing the page.`
        );
      }
    }

    return data as T;
  }

  // ---------------------------------------------------------------------------
  // HTTP Methods
  // ---------------------------------------------------------------------------

  /**
   * GET request with optional schema validation and abort signal.
   */
  async get<T>(url: string, schema?: z.ZodTypeAny, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, { method: 'GET', signal }, schema);
  }

  /**
   * POST request with optional schema validation.
   */
  async post<T>(url: string, body: unknown, schema?: z.ZodTypeAny): Promise<T> {
    return this.request<T>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      schema
    );
  }

  /**
   * PUT request with optional schema validation.
   */
  async put<T>(url: string, body: unknown, schema?: z.ZodTypeAny): Promise<T> {
    return this.request<T>(
      url,
      { method: 'PUT', body: JSON.stringify(body) },
      schema
    );
  }

  /**
   * PATCH request with optional schema validation.
   */
  async patch<T>(url: string, body: unknown, schema?: z.ZodTypeAny): Promise<T> {
    return this.request<T>(
      url,
      { method: 'PATCH', body: JSON.stringify(body) },
      schema
    );
  }

  /**
   * DELETE request with optional schema validation.
   */
  async delete<T>(url: string, schema?: z.ZodTypeAny): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' }, schema);
  }

  /**
   * POST request for SSE / streaming responses.
   *
   * Applies the same auth header injection, proactive token refresh, and
   * 401-retry-after-refresh logic as `_executeRequest`, but returns the raw
   * `Response` so the caller can read the body as a stream.
   *
   * Throws `ApiError` for non-2xx responses (after exhausting the 401 retry).
   */
  async streamPost(url: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<Response> {
    const buildHeaders = (): Record<string, string> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', ...extraHeaders };
      const token = tokenManager.getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return headers;
    };

    // Proactive refresh before the request (same as _executeRequest)
    if (tokenManager.isTokenExpiringSoon() && tokenManager.getRefreshToken()) {
      await tokenManager.refreshAccessToken().catch(() => {});
    }

    let response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(body),
    });

    // 401: attempt one token refresh then retry (mirrors _executeRequest)
    if (response.status === 401 && tokenManager.getAccessToken()) {
      if (tokenManager.getRefreshToken()) {
        try {
          await tokenManager.refreshAccessToken();
          response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(body),
          });
          if (response.status === 401) {
            triggerSessionExpiredRedirect(url);
          }
        } catch {
          triggerSessionExpiredRedirect(url);
        }
      } else {
        triggerSessionExpiredRedirect(url);
      }
    }

    if (!response.ok) {
      const rawText = await response.text().catch(() => response.statusText);
      let detail = rawText;
      try {
        const parsed = JSON.parse(rawText) as {
          detail?: string | Array<{ msg?: string }> | Record<string, unknown>;
        };
        if (typeof parsed.detail === 'string') {
          detail = parsed.detail;
        } else if (Array.isArray(parsed.detail)) {
          // FastAPI/Pydantic validation errors: list of {"loc": [...], "msg": ...}.
          const msgs = parsed.detail.map((item) => item?.msg).filter(Boolean);
          detail = msgs.length ? msgs.join('; ') : 'The request data was invalid.';
        } else if (parsed.detail && typeof parsed.detail === 'object') {
          // Structured error body without a plain message — pick a likely
          // key instead of surfacing the raw JSON object.
          const obj = parsed.detail as Record<string, unknown>;
          const picked = ['message', 'error', 'msg'].map((k) => obj[k]).find((v) => typeof v === 'string');
          detail = (picked as string) || JSON.stringify(obj);
        }
      } catch {
        // Not JSON — use the raw text as-is.
      }
      detail = detail || response.statusText || 'Request failed';
      throw new ApiError(
        response.status,
        url,
        `Chat service error ${response.status}: ${detail}`,
        detail,
      );
    }

    return response;
  }
}

/** Standard client: 60s timeout, 3 retries for cold-start recovery. */
const apiClient = new ApiClient(60_000, 3);

/** AIBOM/asset-service client: 120s timeout for large graph operations, 2 retries. */
const aibomApiClient = new ApiClient(120_000, 2);

// =============================================================================
// Auth API
// =============================================================================

export const authApi = {
  /**
   * Login with email and password.
   */
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    debugLog('[authApi] login start', { email: credentials.email });
    // Validate input
    LoginRequestSchema.parse(credentials);

    const response = await apiClient.post<TokenResponse>(
      `${DATA_SERVICE_URL}/auth/login`,
      credentials,
      TokenResponseSchema
    );

    // Store tokens with expiry time for proactive refresh
    tokenManager.setTokens(response.access_token, response.refresh_token, response.expires_in);
    debugLog('[authApi] login success', { hasAccessToken: Boolean(response.access_token) });

    return response;
  },

  /**
   * Self-service signup: create new tenant + admin user.
   * Returns { status: "pending_verification", email } when email verification is required,
   * or { status: "active", access_token, refresh_token, expires_in } for immediate login (dev mode).
   */
  async signup(data: { name: string; email: string; password: string; org_name?: string }): Promise<{ status: 'pending_verification'; email: string } | TokenResponse> {
    const raw = await apiClient.post<Record<string, unknown>>(
      `${DATA_SERVICE_URL}/auth/signup`,
      data,
      z.record(z.unknown())
    );
    if (raw['status'] === 'pending_verification') {
      return { status: 'pending_verification', email: raw['email'] as string };
    }
    // Active (no email config) — parse as TokenResponse and store tokens
    const response = TokenResponseSchema.parse(raw);
    tokenManager.setTokens(response.access_token, response.refresh_token, response.expires_in);
    return response;
  },

  /**
   * Logout and clear tokens.
   */
  async logout(): Promise<void> {
    debugLog('[authApi] logout start');
    try {
      await apiClient.post(`${DATA_SERVICE_URL}/auth/logout`, {});
    } catch {
      // Ignore errors on logout
    } finally {
      tokenManager.clearTokens();
      debugLog('[authApi] logout complete');
    }
  },

  /**
   * Get current user info.
   */
  async getCurrentUser(): Promise<User> {
    debugLog('[authApi] getCurrentUser start');
    return apiClient.get<User>(`${DATA_SERVICE_URL}/auth/me`, UserSchema);
  },

  /**
   * Check if user is authenticated.
   */
  isAuthenticated(): boolean {
    return tokenManager.isAuthenticated();
  },

  /**
   * Change current user's password.
   */
  async changePassword(password: string): Promise<void> {
    await apiClient.patch(`${DATA_SERVICE_URL}/users/me/password`, { password });
  },

  /**
   * Request a password reset email. Always resolves — never reveals whether email exists.
   */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post(`${DATA_SERVICE_URL}/auth/forgot-password`, { email }, z.unknown());
  },

  /**
   * Complete a password reset using the signed token from the reset email.
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post(
      `${DATA_SERVICE_URL}/auth/reset-password`,
      { token, new_password: newPassword },
      z.unknown(),
    );
  },

  /**
   * Resend the email verification link for an unverified account.
   * Always resolves — never reveals whether email exists.
   */
  async resendVerification(email: string): Promise<void> {
    await apiClient.post(`${DATA_SERVICE_URL}/auth/resend-verification`, { email }, z.unknown());
  },
};

// =============================================================================
// Applications API
// =============================================================================

export const applicationsApi = {
  /**
   * List all applications for the current tenant.
   * Phase 1: moved to /api/assets/applications (ai_asset_service).
   */
  async list(): Promise<Application[]> {
    return apiClient.get<Application[]>(
      `${ASSETS_SERVICE_URL}/applications`,
      z.array(ApplicationSchema)
    );
  },

  /**
   * Get a single application by ID.
   * Phase 1: moved to /api/assets/applications (ai_asset_service).
   */
  async get(id: string): Promise<Application> {
    return apiClient.get<Application>(
      `${ASSETS_SERVICE_URL}/applications/${id}`,
      ApplicationSchema
    );
  },

  /**
   * Create a new application.
   * Phase 1: moved to /api/assets/applications (ai_asset_service).
   */
  async create(data: ApplicationCreate): Promise<Application> {
    ApplicationCreateSchema.parse(data);
    return apiClient.post<Application>(
      `${ASSETS_SERVICE_URL}/applications`,
      data,
      ApplicationSchema
    );
  },

  /**
   * Ensure application exists or create it.
   * @param name Application name
   * @param repositoryUrl Optional repository URL for deduplication
   * Phase 1: moved to /api/assets/applications/ensure (ai_asset_service).
   */
  async ensure(name: string, repositoryUrl?: string): Promise<Application> {
    return apiClient.put<Application>(
      `${ASSETS_SERVICE_URL}/applications/ensure`,
      { name, repository_url: repositoryUrl || null },
      ApplicationSchema
    );
  },

  /**
   * Check whether an application already exists for this tenant with the
   * given repository URL, without creating anything. Used by "Add
   * Application" to warn before ensure()/createScan() would silently reuse
   * an existing app.
   */
  async checkDuplicate(repositoryUrl: string): Promise<
    { exists: false } | { exists: true; application_id: string; application_name: string }
  > {
    return apiClient.get(
      `${ASSETS_SERVICE_URL}/applications/check-duplicate?repository_url=${encodeURIComponent(repositoryUrl)}`,
    );
  },

  /**
   * Pre-flight check that a GitHub repository URL is actually reachable
   * (and that any supplied access token is valid) before ensure()/createScan()
   * are called. Does not create anything.
   */
  async checkAccessible(
    repositoryUrl: string,
    githubToken?: string
  ): Promise<{ accessible: boolean; reason?: string; message?: string }> {
    return apiClient.post(
      `${ASSETS_SERVICE_URL}/applications/check-accessible`,
      { repository_url: repositoryUrl, github_token: githubToken || null },
    );
  },

  /**
   * Update an application.
   * Phase 1: moved to /api/assets/applications (ai_asset_service).
   */
  async update(id: string, data: Partial<ApplicationCreate>): Promise<Application> {
    return apiClient.patch<Application>(
      `${ASSETS_SERVICE_URL}/applications/${id}`,
      data,
      ApplicationSchema
    );
  },

  /**
   * Delete an application.
   * Phase 1: moved to /api/assets/applications (ai_asset_service).
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ASSETS_SERVICE_URL}/applications/${id}`);
  },

  /**
   * Get the latest cognitive policy for an application.
   * Generated automatically after each completed AIBOM scan.
   * Returns null if no policy exists yet (404) or on any error.
   */
  async getCognitivePolicy(id: string, scanId?: string): Promise<{
    id: string;
    application_id: string;
    scan_id: string;
    status: 'generating' | 'completed' | 'failed';
    content_markdown: string | null;
    error_message: string | null;
    updated_by: string | null;
    created_at: string | null;
    updated_at: string | null;
  } | null> {
    try {
      const params = scanId ? `?scan_id=${encodeURIComponent(scanId)}` : '';
      return await apiClient.get(`${ASSETS_SERVICE_URL}/applications/${id}/cognitive-policy${params}`);
    } catch (err: unknown) {
      const status = getErrorStatus(err);

      // If a specific scan has no policy yet, fall back to the latest available policy.
      if (scanId && status === 404) {
        try {
          return await apiClient.get(`${ASSETS_SERVICE_URL}/applications/${id}/cognitive-policy`);
        } catch (fallbackErr: unknown) {
          const fallbackStatus = getErrorStatus(fallbackErr);
          if (fallbackStatus === 404) return null;
          throw fallbackErr;
        }
      }

      if (status === 404) return null;
      throw err;
    }
  },

  async regenerateCognitivePolicy(id: string): Promise<{ message: string; scan_id: string }> {
    try {
      return await apiClient.post(`${ASSETS_SERVICE_URL}/applications/${id}/cognitive-policy/regenerate`, {});
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          throw new Error(
            err.detail ||
            'No completed AIBOM scan exists for this application yet. Run a discovery scan first, then retry regeneration.'
          );
        }

        if (err.status === 403) {
          throw new Error(err.detail || 'You need editor access to regenerate a cognitive policy.');
        }
      }

      throw err;
    }
  },

  async updateCognitivePolicy(id: string, contentMarkdown: string): Promise<{
    id: string;
    application_id: string;
    scan_id: string;
    status: string;
    content_markdown: string | null;
    error_message: string | null;
    updated_by: string | null;
    created_at: string | null;
    updated_at: string | null;
  }> {
    return await apiClient.put(`${ASSETS_SERVICE_URL}/applications/${id}/cognitive-policy`, { content_markdown: contentMarkdown });
  },

  /**
   * Get policies assessed against a specific application.
   */
  async getPolicies(id: string): Promise<Array<{
    id: string;
    name: string;
    description: string | null;
    category: string;
    total_controls?: number;
  }>> {
    return apiClient.get(`${ASSETS_SERVICE_URL}/applications/${id}/policies`);
  },

  /**
   * Get behavior and red team run findings summary for a specific application.
   * Counts are from the most recent completed run of each type.
   */
  async getRunSummary(id: string): Promise<{
    behavior_findings: number;
    behavior_critical: number;
    behavior_high: number;
    behavior_runs_total: number;
    redteam_findings: number;
    redteam_critical: number;
    redteam_high: number;
    redteam_runs_total: number;
  }> {
    return apiClient.get(`${ASSETS_SERVICE_URL}/applications/${id}/findings`);
  },

  /**
   * Get findings for a specific application.
   * @deprecated The /findings endpoint now returns run summary KPIs. Use getRunSummary instead.
   */
  async getFindings(id: string): Promise<{
    items: Array<{
      id: string;
      title: string;
      description: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      gap_type: string;
      control_id: string;
      control_name: string;
      policy_name: string;
      affected_file: string;
      line_number: number;
      remediation: string;
      confidence_score: number;
    }>;
  }> {
    return apiClient.get(`${ASSETS_SERVICE_URL}/applications/${id}/findings`);
  },
};

// =============================================================================
// Policies API
// =============================================================================

export const policiesApi = {
  /**
   * List all policies (system-wide + tenant-specific).
   */
  async list(): Promise<Policy[]> {
    console.log('[policiesApi.list] Fetching policies...');
    const policies = await apiClient.get<Policy[]>(
      `${COMPLIANCE_SERVICE_URL}/policies`,
      z.array(PolicySchema)
    );
    console.log(`[policiesApi.list] Received ${policies.length} policies`);
    policies.forEach((p: Policy) => {
      console.log(`[policiesApi.list] Policy '${p.name}': ${p.controls?.length ?? 0} controls`);
    });
    return policies;
  },

  /**
   * Get a single policy by ID.
   */
  async get(id: string): Promise<Policy> {
    return apiClient.get<Policy>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}`,
      PolicySchema
    );
  },

  /**
   * Create a new custom policy.
   */
  async create(data: PolicyCreate): Promise<Policy> {
    PolicyCreateSchema.parse(data);
    return apiClient.post<Policy>(
      `${COMPLIANCE_SERVICE_URL}/policies`,
      data,
      PolicySchema
    );
  },

  /**
   * Create a policy from a NuGuard Standard JSON blob.
   * Controls are upserted directly — no LLM extraction step needed.
   */
  async createStructured(data: {
    name: string;
    description?: string;
    category?: string;
    enabled?: boolean;
    format: 'nuguard_standard';
    framework_json: Record<string, unknown>;
  }): Promise<Policy> {
    return apiClient.post<Policy>(
      `${COMPLIANCE_SERVICE_URL}/policies/structured`,
      data,
      PolicySchema
    );
  },

  /**
   * Update a policy.
   */
  async update(id: string, data: PolicyUpdate): Promise<Policy> {
    PolicyUpdateSchema.partial().parse(data);
    return apiClient.patch<Policy>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}`,
      data,
      PolicySchema
    );
  },

  /**
   * Delete a policy (custom policies only).
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}`);
  },

  /**
   * Toggle policy enabled state.
   * Uses dedicated toggle endpoint that works for both system and custom policies.
   */
  async toggle(id: string, enabled: boolean): Promise<Policy> {
    return apiClient.patch<Policy>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}/toggle?enabled=${enabled}`,
      {},
      PolicySchema
    );
  },

  /**
   * Get all controls for a policy.
   * 
   * @param id - Policy ID to get controls for
   * @param aiApplicableOnly - If true, only return controls marked as AI-applicable
   * @returns List of controls for the policy
   */
  async getControls(id: string, aiApplicableOnly = false): Promise<Control[]> {
    const params = aiApplicableOnly ? '?ai_applicable_only=true' : '';
    return apiClient.get<Control[]>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}/controls${params}`,
      z.array(ControlSchema)
    );
  },

  /**
   * Update a single control within a custom policy.
   * Editing name or description triggers automatic AI re-enrichment of
   * gap/mitigation recommendations.
   */
  async updateControl(
    policyId: string,
    controlId: string,
    data: { name?: string; description?: string; severity?: string },
  ): Promise<PolicyControl> {
    return apiClient.patch<PolicyControl>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(policyId)}/controls/${encodeURIComponent(controlId)}`,
      data,
      PolicyControlSchema,
    );
  },

  /**
   * Delete a single control from a custom policy.
   */
  async deleteControl(policyId: string, controlId: string): Promise<void> {
    await apiClient.delete(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(policyId)}/controls/${encodeURIComponent(controlId)}`,
    );
  },

  /**
   * Get the number of scans that used a specific policy.
   * 
   * @param id - Policy ID to get scan count for
   * @returns Object with scan_count field
   */
  async getScanCount(id: string): Promise<{ scan_count: number }> {
    return apiClient.get<{ scan_count: number }>(
      `${COMPLIANCE_SERVICE_URL}/policies/${encodeURIComponent(id)}/scan-count`,
      z.object({ scan_count: z.number() })
    );
  },
};

// =============================================================================
// Scans API
// =============================================================================

export const scansApi = {
  /**
   * List all scans for the current tenant.
   * Returns lightweight scan objects without full details.
   */
  async list(applicationId?: string): Promise<ScanListItem[]> {
    const params = applicationId ? `?application_id=${applicationId}` : '';
    return apiClient.get<ScanListItem[]>(
      `${DATA_SERVICE_URL}/scans${params}`,
      z.array(ScanListItemSchema)
    );
  },

  /**
   * Get a single scan by ID with full details.
   * Includes findings, red team attacks, and detected assets.
   */
  async get(id: string): Promise<ScanResponse> {
    return apiClient.get<ScanResponse>(
      `${DATA_SERVICE_URL}/scans/${id}`,
      ScanResponseSchema
    );
  },

  /**
   * Get real-time status of a scan.
   * Polls the asset service (scans are owned by ai_asset_service).
   * Maps AIBOMScanResponse to ScanStatusResponse for compatibility.
   *
   * @param id - AIBOM scan ID to check status for
   * @returns Current scan status with phase, counts, and risk score
   */
  async getStatus(id: string): Promise<ScanStatusResponse> {
    const scan = await aibomApiClient.get<AIBOMScanResponse>(
      `${ASSETS_SERVICE_URL}/scans/${id}`,
      AIBOMScanResponseSchema
    );
    return {
      id: scan.id,
      status: scan.status,
      // AIBOM scans use status for both status and phase (no separate phase field)
      phase: scan.status,
      phase_message: scan.error_message || null,
      assets_discovered_count: scan.node_count || 0,
      findings_count: 0,
      risk_score: 0,
      risk_band: 'LOW',
    };
  },

  /**
   * Poll scan status until completion or timeout.
   * Useful for tracking GitHub scan progress in real-time.
   * 
   * @param id - Scan ID to poll
   * @param onProgress - Callback for status updates
   * @param intervalMs - Polling interval in milliseconds (default: 2000)
   * @param timeoutMs - Maximum time to poll in milliseconds (default: 600000 = 10 min)
   * @returns Final scan response when completed
   */
  async pollUntilComplete(
    id: string,
    onProgress?: (status: ScanStatusResponse) => void,
    intervalMs = 2000,
    timeoutMs = 600000,
    signal?: AbortSignal,
  ): Promise<ScanResponse> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      if (signal?.aborted) throw new DOMException('Scan polling aborted', 'AbortError');

      const status = await this.getStatus(id);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }

      // Check if scan is complete or failed (status normalized to UPPERCASE by Zod schema)
      // DISCOVERY_COMPLETE is the terminal state for AIBOM-only scans (two-phase design)
      if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'DISCOVERY_COMPLETE') {
        // Return an asset-service-based ScanResponse compatible shape
        const aibomScan = await aibomApiClient.get<AIBOMScanResponse>(
          `${ASSETS_SERVICE_URL}/scans/${id}`,
          AIBOMScanResponseSchema
        );
        return {
          id: aibomScan.id,
          target_name: `GitHub: ${aibomScan.github_url?.split('/').pop() ?? id}`,
          target_type: 'GITHUB_REPO' as const,
          github_url: aibomScan.github_url ?? null,
          github_branch: aibomScan.github_branch ?? null,
          github_commit_sha: aibomScan.github_commit_sha ?? null,
          phase: aibomScan.status,
          phase_message: aibomScan.error_message ?? null,
          risk_score: 0,
          risk_band: 'LOW' as const,
          status: aibomScan.status,
          application_id: aibomScan.application_id,
          application_name: '',
          tenant_id: aibomScan.tenant_id,
          assets_discovered_count: aibomScan.node_count || 0,
          findings_count: 0,
          policies_analyzed: [],
          findings: [],
          covered_controls: [],
          red_team_attacks: [],
          detected_assets: [],
          risk_summary: null,
          created_at: aibomScan.created_at,
        } as ScanResponse;
      }

      // Wait before next poll (abort-aware)
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, intervalMs);
        if (signal) {
          signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Scan polling aborted', 'AbortError')); }, { once: true });
        }
      });
    }

    // Timeout reached — return latest AIBOM scan state
    console.warn(`Scan polling timeout after ${timeoutMs}ms for scan ${id}`);
    const aibomScan = await aibomApiClient.get<AIBOMScanResponse>(
      `${ASSETS_SERVICE_URL}/scans/${id}`,
      AIBOMScanResponseSchema
    );
    return {
      id: aibomScan.id,
      target_name: `GitHub: ${aibomScan.github_url?.split('/').pop() ?? id}`,
      target_type: 'GITHUB_REPO' as const,
      github_url: aibomScan.github_url ?? null,
      github_branch: aibomScan.github_branch ?? null,
      github_commit_sha: aibomScan.github_commit_sha ?? null,
      phase: aibomScan.status,
      phase_message: aibomScan.error_message ?? null,
      risk_score: 0,
      risk_band: 'LOW' as const,
      status: aibomScan.status,
      application_id: aibomScan.application_id,
      application_name: '',
      tenant_id: aibomScan.tenant_id,
      assets_discovered_count: aibomScan.node_count || 0,
      findings_count: 0,
      policies_analyzed: [],
      findings: [],
      covered_controls: [],
      red_team_attacks: [],
      detected_assets: [],
      risk_summary: null,
      created_at: aibomScan.created_at,
    } as ScanResponse;
  },

  /**
   * Delete a scan.
   * Also deletes associated findings, attacks, and assets.
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${DATA_SERVICE_URL}/scans/${id}`);
  },
};

// =============================================================================
// Risk Catalog API (phase 1 — data_service /risk-catalog, flag-gated)
// =============================================================================

export interface RiskCatalogListParams {
  applicationId?: string;
  severity?: string;
  goalType?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export const riskCatalogApi = {
  /**
   * List deduplicated behavioral records (grouped validation_findings).
   * Returns 404 when the backend feature flag is off.
   */
  async listRecords(params: RiskCatalogListParams = {}): Promise<RiskCatalogListResponse> {
    const qs = new URLSearchParams();
    if (params.applicationId) qs.set('application_id', params.applicationId);
    if (params.severity) qs.set('severity', params.severity);
    if (params.goalType) qs.set('goal_type', params.goalType);
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get<RiskCatalogListResponse>(
      `${DATA_SERVICE_URL}/risk-catalog/records${suffix}`,
      RiskCatalogListResponseSchema
    );
  },

  /**
   * Get one record's full detail: backing findings, runs, and coverage.
   */
  async getRecord(recordId: string): Promise<RiskCatalogRecordDetail> {
    return apiClient.get<RiskCatalogRecordDetail>(
      `${DATA_SERVICE_URL}/risk-catalog/records/${encodeURIComponent(recordId)}`,
      RiskCatalogRecordDetailSchema
    );
  },
};

// =============================================================================
// Assets API
// =============================================================================

export const assetsApi = {
  /**
   * List all AI assets for the current tenant.
   */
  async list(applicationId?: string): Promise<AIAsset[]> {
    const params = applicationId ? `?application_id=${applicationId}` : '';
    return apiClient.get<AIAsset[]>(
      `${DATA_SERVICE_URL}/assets${params}`,
      z.array(AIAssetSchema)
    );
  },

};

// =============================================================================
// Users API
// =============================================================================

export const usersApi = {
  /**
   * List all users for the current tenant (admin only).
   */
  async list(): Promise<User[]> {
    return apiClient.get<User[]>(
      `${DATA_SERVICE_URL}/users`,
      z.array(UserSchema)
    );
  },

  /**
   * Create a new user (admin only).
   */
  async create(data: UserCreate): Promise<User> {
    return apiClient.post<User>(
      `${DATA_SERVICE_URL}/users`,
      data,
      UserSchema
    );
  },

  /**
   * Update a user (admin only).
   */
  async update(id: string, data: UserUpdate): Promise<User> {
    return apiClient.patch<User>(
      `${DATA_SERVICE_URL}/users/${id}`,
      data,
      UserSchema
    );
  },

  /**
   * Delete a user (admin only).
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${DATA_SERVICE_URL}/users/${id}`);
  },
};

// =============================================================================
// Audit Logs API
// =============================================================================

/**
 * Paginated audit logs response
 */
export interface AuditLogPaginatedResponse {
  logs: AuditLog[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AuditLogFilters {
  dateFrom?: string | null;       // ISO string
  dateTo?: string | null;         // ISO string
  applicationIds?: string[];      // [] = all
  actions?: string[];             // [] = all
  userIds?: string[];             // [] = all
}

const AuditLogPaginatedResponseSchema = z.object({
  logs: z.array(AuditLogSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number()
});

export const auditLogsApi = {
  /**
   * List audit logs for the current tenant with pagination and optional filters.
   * Logs are retained for 60 days.
   */
  async list(page = 1, pageSize = 25, filters: AuditLogFilters = {}): Promise<AuditLogPaginatedResponse> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filters.dateFrom)               params.set('date_from',        filters.dateFrom);
    if (filters.dateTo)                 params.set('date_to',          filters.dateTo);
    if (filters.applicationIds?.length) params.set('application_ids',  filters.applicationIds.join(','));
    if (filters.actions?.length)        params.set('actions',          filters.actions.join(','));
    if (filters.userIds?.length)        params.set('user_ids',         filters.userIds.join(','));
    return apiClient.get<AuditLogPaginatedResponse>(
      `${DATA_SERVICE_URL}/audit-logs?${params}`,
      AuditLogPaginatedResponseSchema
    );
  },

  /** Return distinct action codes present in this tenant's audit logs (last 60 days). */
  async listActions(): Promise<string[]> {
    return apiClient.get<string[]>(
      `${DATA_SERVICE_URL}/audit-logs/actions`,
      z.array(z.string())
    );
  },

  /**
   * Export all matching audit logs as a CSV Blob.
   * Accepts the same filters as list(). The blob is suitable for a browser
   * download via <a download>. Requires admin role.
   */
  async exportCsv(filters: AuditLogFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters.dateFrom)               params.set('date_from',        filters.dateFrom);
    if (filters.dateTo)                 params.set('date_to',          filters.dateTo);
    if (filters.applicationIds?.length) params.set('application_ids',  filters.applicationIds.join(','));
    if (filters.actions?.length)        params.set('actions',          filters.actions.join(','));
    if (filters.userIds?.length)        params.set('user_ids',         filters.userIds.join(','));

    const token = tokenManager.getAccessToken();
    const response = await fetch(
      `${DATA_SERVICE_URL}/audit-logs/export?${params}`,
      {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      }
    );

    if (!response.ok) {
      let errorMessage = `Export failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData?.detail) errorMessage = errorData.detail;
      } catch { /* non-JSON body */ }
      throw new Error(errorMessage);
    }

    return response.blob();
  },
};

// =============================================================================
// Reports API
// =============================================================================

/**
 * Report response from API.
 */
export interface ReportResponse {
  id: string;
  tenant_id: string;
  scan_id: string | null;
  application_id: string | null;
  title: string;
  report_type: string;
  format: string;
  status: ReportStatus;
  file_size_bytes: number | null;
  sha256_hash: string | null;
  download_url: string | null;
  created_at: string;
  generated_at: string | null;
  error_message: string | null;
  report_profile?: string | null;
  audience?: string | null;
  source_summary?: {
    source_mode?: string;
    date_from?: string | null;
    date_to?: string | null;
    behavior?: {
      included?: boolean;
      available?: boolean;
      run_id?: string | null;
      findings?: number;
    };
    redteam?: {
      included?: boolean;
      available?: boolean;
      run_id?: string | null;
      findings?: number;
    };
  } | null;
}

/**
 * Paginated reports list response.
 */
export interface ReportListResponse {
  reports: ReportResponse[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Request to create/generate a new report.
 */
export interface ReportCreate {
  scan_id?: string;
  application_id?: string;
  report_type: string;
  format?: string;
  title?: string;
  parameters?: Record<string, unknown>;
}

/**
 * Request to generate reports from a scan.
 */
export interface ScanReportGenerateRequest {
  report_types?: string[];
}

export const reportsApi = {
  /**
   * List reports with optional filtering and pagination.
   * 
   * @param params - Filter and pagination parameters
   * @returns Paginated list of reports
   */
  async list(params?: {
    application_id?: string;
    scan_id?: string;
    report_type?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<ReportListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.application_id) searchParams.set('application_id', params.application_id);
    if (params?.scan_id) searchParams.set('scan_id', params.scan_id);
    if (params?.report_type) searchParams.set('report_type', params.report_type);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.page_size) searchParams.set('page_size', params.page_size.toString());
    
    const queryString = searchParams.toString();
    const url = `${DATA_SERVICE_URL}/reports${queryString ? `?${queryString}` : ''}`;
    
    return apiClient.get<ReportListResponse>(url);
  },

  /**
   * Get a single report by ID.
   * 
   * @param id - Report ID
   * @returns Report details including download URL if completed
   */
  async get(id: string): Promise<ReportResponse> {
    return apiClient.get<ReportResponse>(
      `${DATA_SERVICE_URL}/reports/${encodeURIComponent(id)}`
    );
  },

  /**
   * Create and generate a new report.
   * Report generation happens asynchronously - poll GET /reports/{id} for status.
   *
   * @param data - Report creation parameters
   * @returns Initial report record with PENDING status
   */
  async create(data: ReportCreate): Promise<ReportResponse> {
    return apiClient.post<ReportResponse>(
      `${DATA_SERVICE_URL}/reports`,
      data
    );
  },

  /**
   * Delete a report and its associated blob storage.
   * 
   * @param id - Report ID to delete
   */
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `${DATA_SERVICE_URL}/reports/${encodeURIComponent(id)}`
    );
  },

  /**
   * Download a report file as a Blob.
   *
   * @param id - Report ID to download
   * @returns Blob payload for the downloaded file
   */
  async downloadBlob(id: string): Promise<Blob> {
    const token = tokenManager.getAccessToken();
    const response = await fetch(
      `${DATA_SERVICE_URL}/reports/${encodeURIComponent(id)}/download`,
      {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      }
    );

    if (!response.ok) {
      let errorMessage = `Download failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData?.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Non-JSON error body; keep default message.
      }
      throw new Error(errorMessage);
    }

    return response.blob();
  },

  /**
   * Download a report file directly.
   * Returns a blob URL that can be used for download.
   * 
   * @param id - Report ID to download
   * @returns Blob URL for the downloaded file
   */
  async download(id: string): Promise<string> {
    const blob = await reportsApi.downloadBlob(id);
    return URL.createObjectURL(blob);
  },

  /**
   * Generate standard reports for a completed scan.
   * Creates multiple report types (Executive, Technical, Compliance, SARIF).
   * 
   * @param scanId - Scan ID to generate reports for
   * @param reportTypes - Optional list of specific report types to generate
   * @returns List of created report records
   */
  async generateFromScan(
    scanId: string, 
    reportTypes?: string[]
  ): Promise<ReportResponse[]> {
    const request: ScanReportGenerateRequest = { report_types: reportTypes };
    return apiClient.post<ReportResponse[]>(
      `${DATA_SERVICE_URL}/reports/scan/${encodeURIComponent(scanId)}/generate`,
      request
    );
  },
};

// =============================================================================
// Evidence Export API (Phase 2C)
// =============================================================================

/**
 * Request to export a scan's compliance package.
 */
export interface ExportScanRequest {
  scan_id: string;
  include_assets?: boolean;
  include_attacks?: boolean;
  frameworks?: string[];
}

/**
 * Response from export operation.
 */
export interface ExportResult {
  success: boolean;
  export_id: string;
  export_path: string;
  zip_path: string | null;
  files_count: number;
  error: string | null;
}

/**
 * Export listing item.
 */
export interface ExportItem {
  export_id: string;
  scan_id: string;
  created_at: string;
  files_count: number;
  zip_path: string | null;
}

export const exportsApi = {
  /**
   * Export scan results to a compliance package.
   * Creates a ZIP with executive summary, findings, assets, and attacks.
   * 
   * @param scanId - The scan ID to export
   * @param options - Optional configuration
   */
  async exportScan(
    scanId: string,
    options?: { includeAssets?: boolean; includeAttacks?: boolean; frameworks?: string[] }
  ): Promise<ExportResult> {
    const request: ExportScanRequest = {
      scan_id: scanId,
      include_assets: options?.includeAssets ?? true,
      include_attacks: options?.includeAttacks ?? true,
      frameworks: options?.frameworks,
    };
    // DATA_SERVICE_URL preserves /api/data in both dev and gateway routing.
    return apiClient.post<ExportResult>(`${DATA_SERVICE_URL}/evidence/export/scan`, request);
  },

  /**
   * List exports for the current tenant.
   * Optionally filter by scan_id.
   */
  async list(scanId?: string): Promise<{ exports: ExportItem[]; total: number }> {
    const params = scanId ? `?scan_id=${scanId}` : '';
    return apiClient.get<{ exports: ExportItem[]; total: number }>(
      `${DATA_SERVICE_URL}/evidence/exports${params}`
    );
  },

  /**
   * Get export manifest with file hashes.
   */
  async getManifest(exportId: string, scanId: string): Promise<unknown> {
    return apiClient.get(`${DATA_SERVICE_URL}/evidence/export/${exportId}/manifest?scan_id=${scanId}`);
  },

  /**
   * Download export ZIP file.
   * Returns a blob URL for direct download.
   */
  async downloadZip(exportId: string, scanId: string): Promise<string> {
    const token = tokenManager.getAccessToken();
    const response = await fetch(
      `${DATA_SERVICE_URL}/evidence/download/${exportId}?scan_id=${scanId}`,
      {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      }
    );
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Delete an export package.
   */
  async delete(exportId: string, scanId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `${DATA_SERVICE_URL}/evidence/export/${exportId}?scan_id=${scanId}`
    );
  },
};

// =============================================================================
// AIBOM API (ai_asset_service)
// =============================================================================

export const aibomApi = {
  // -------------------------------------------------------------------------
  // Scans
  // -------------------------------------------------------------------------
  
  /**
   * Trigger AIBOM extraction for an application.
   */
  async createScan(data: AIBOMScanCreate): Promise<AIBOMScanResponse> {
    return aibomApiClient.post<AIBOMScanResponse>(
      `${ASSETS_SERVICE_URL}/scans`,
      data,
      AIBOMScanResponseSchema
    );
  },

  /**
   * Trigger local AIBOM extraction by uploading selected folder files.
   *
   * Files are filtered before upload to avoid sending binaries, oversized files,
   * or irrelevant files (e.g. node_modules content picked accidentally). This
   * keeps the payload within a range the Vite dev-proxy and nginx can handle.
   */
  async createLocalUploadScan(
    applicationId: string,
    files: File[],
    folderName?: string
  ): Promise<AIBOMScanResponse> {
    // Extensions the backend can extract meaningful AI-asset information from.
    const ALLOWED_EXTENSIONS = new Set([
      '.py', '.ipynb', '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs',
      '.java', '.go', '.rb', '.php', '.cs', '.cpp', '.cc', '.c', '.h', '.hpp',
      '.rs', '.swift', '.kt', '.scala', '.sql', '.sh',
      '.yaml', '.yml', '.toml', '.json', '.md', '.txt', '.env',
    ]);
    const MAX_FILES = 500;
    const MAX_FILE_BYTES = 1024 * 1024; // 1 MB — matches backend MAX_FILE_BYTES

    const filteredFiles = files
      .filter(f => {
        const dotIdx = f.name.lastIndexOf('.');
        const ext = dotIdx >= 0 ? f.name.slice(dotIdx).toLowerCase() : '';
        return ALLOWED_EXTENSIONS.has(ext) && f.size <= MAX_FILE_BYTES;
      })
      .slice(0, MAX_FILES);

    if (filteredFiles.length === 0) {
      throw new ApiError(400, `${ASSETS_SERVICE_URL}/scans/local-upload`,
        'No supported source files found. Select a folder containing Python, TypeScript, or JavaScript files.');
    }

    const form = new FormData();
    form.append('application_id', applicationId);
    if (folderName) form.append('folder_name', folderName);
    for (const file of filteredFiles) {
      const relPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      form.append('files', file, relPath);
    }

    const token = tokenManager.getAccessToken();
    const response = await fetch(`${ASSETS_SERVICE_URL}/scans/local-upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = typeof errorData?.detail === 'string'
        ? errorData.detail
        : `API Error: ${response.status}`;
      throw new ApiError(response.status, `${ASSETS_SERVICE_URL}/scans/local-upload`, detail);
    }

    return AIBOMScanResponseSchema.parse(await response.json());
  },

  /**
   * Ingest a pre-built NuGuard AIBOM JSON file.
   * The backend validates format with AiSbomDocument.model_validate() and returns 400 on failure.
   */
  async createSbomUploadScan(
    applicationId: string,
    file: File,
  ): Promise<AIBOMScanResponse> {
    const form = new FormData();
    form.append('application_id', applicationId);
    form.append('file', file, file.name);

    const token = tokenManager.getAccessToken();
    const response = await fetch(`${ASSETS_SERVICE_URL}/scans/sbom-upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = typeof errorData?.detail === 'string'
        ? errorData.detail
        : `API Error: ${response.status}`;
      throw new ApiError(response.status, `${ASSETS_SERVICE_URL}/scans/sbom-upload`, detail);
    }

    return AIBOMScanResponseSchema.parse(await response.json());
  },

  /**
   * Get AIBOM scan by ID.
   */
  async getScan(scanId: string): Promise<AIBOMScanResponse> {
    return aibomApiClient.get<AIBOMScanResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}`,
      AIBOMScanResponseSchema
    );
  },

  /**
   * Get computed scan-level summary (frameworks, deployment context, endpoints).
   */
  async getScanSummary(scanId: string): Promise<AIBOMScanSummaryResponse> {
    return aibomApiClient.get<AIBOMScanSummaryResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/summary`,
      AIBOMScanSummaryResponseSchema
    );
  },

  /**
   * List AIBOM scans for an application.
   */
  async listScans(applicationId?: string, page = 1, pageSize = 20): Promise<AIBOMScanListResponse> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (applicationId) params.append('application_id', applicationId);
    return aibomApiClient.get<AIBOMScanListResponse>(
      `${ASSETS_SERVICE_URL}/scans?${params}`,
      AIBOMScanListResponseSchema
    );
  },

  // -------------------------------------------------------------------------
  // Nodes
  // -------------------------------------------------------------------------

  /**
   * List nodes for a scan.
   */
  async listNodes(scanId: string, nodeType?: string, page = 1, pageSize = 50): Promise<AIBOMNodeListResponse> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (nodeType) params.append('node_type', nodeType);
    return aibomApiClient.get<AIBOMNodeListResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/nodes?${params}`,
      AIBOMNodeListResponseSchema
    );
  },

  /**
   * Get node by ID.
   */
  async getNode(nodeId: string): Promise<AIBOMNodeResponse> {
    return aibomApiClient.get<AIBOMNodeResponse>(
      `${ASSETS_SERVICE_URL}/nodes/${nodeId}`,
      AIBOMNodeResponseSchema
    );
  },

  /**
   * Get node type counts for a scan.
   */
  async getNodeTypeCounts(scanId: string): Promise<NodeTypeCount[]> {
    return aibomApiClient.get<NodeTypeCount[]>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/nodes/types`,
      z.array(NodeTypeCountSchema)
    );
  },

  // -------------------------------------------------------------------------
  // Edges
  // -------------------------------------------------------------------------

  /**
   * List edges for a scan.
   */
  async listEdges(scanId: string): Promise<AIBOMEdgeListResponse> {
    return aibomApiClient.get<AIBOMEdgeListResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/edges`,
      AIBOMEdgeListResponseSchema
    );
  },

  // -------------------------------------------------------------------------
  // Evidence
  // -------------------------------------------------------------------------

  /**
   * Get evidence for a node.
   */
  async getNodeEvidence(nodeId: string): Promise<AIBOMEvidenceListResponse> {
    return aibomApiClient.get<AIBOMEvidenceListResponse>(
      `${ASSETS_SERVICE_URL}/nodes/${nodeId}/evidence`,
      AIBOMEvidenceListResponseSchema
    );
  },

  // -------------------------------------------------------------------------
  // Graph
  // -------------------------------------------------------------------------

  /**
   * Get full AIBOM graph for a scan (nodes + edges in D3 format).
   */
  async getGraph(scanId: string): Promise<AIBOMGraphResponse> {
    return aibomApiClient.get<AIBOMGraphResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/graph`,
      AIBOMGraphResponseSchema
    );
  },

  /**
   * Get the package dependency map for a scan.
   * Only populated for nuguard-sbom-extractor scans; returns empty deps list otherwise.
   *
   * @param scanId - AIBOM scan ID
   * @param group  - Optional group filter prefix: "runtime", "dev", "optional"
   */
  async getScanDependencies(scanId: string, group?: string): Promise<ScanDependenciesResponse> {
    const params = new URLSearchParams();
    if (group) params.set('group', group);
    const qs = params.toString() ? `?${params}` : '';
    return aibomApiClient.get<ScanDependenciesResponse>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/dependencies${qs}`,
      ScanDependenciesResponseSchema
    );
  },

  /**
   * Poll an AIBOM scan until it reaches a terminal state.
   * Terminal states: COMPLETED, DISCOVERY_COMPLETE, FAILED.
   *
   * @param scanId - AIBOM scan ID to poll
   * @param signal - Optional AbortSignal for cancellation
   * @param intervalMs - Polling interval (default: 2000ms)
   * @param timeoutMs - Timeout (default: 300s)
   */
  async pollScanUntilReady(
    scanId: string,
    signal?: AbortSignal,
    intervalMs = 2000,
    timeoutMs = 300_000,
  ): Promise<AIBOMScanResponse> {
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      if (signal?.aborted) throw new DOMException('Scan polling aborted', 'AbortError');
      const scan = await this.getScan(scanId);
      if (['COMPLETED', 'DISCOVERY_COMPLETE', 'FAILED'].includes(scan.status)) return scan;
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, intervalMs);
        signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
      });
    }
    return this.getScan(scanId);
  },
};

// =============================================================================
// Compliance API (assessment_service)
// =============================================================================

export const complianceApi = {
  // -------------------------------------------------------------------------
  // Assessments
  // -------------------------------------------------------------------------

  /**
   * Create a new compliance assessment.
   */
  async createAssessment(data: AssessmentCreate): Promise<AssessmentResponse> {
    return apiClient.post<AssessmentResponse>(
      `${COMPLIANCE_SERVICE_URL}/assessments`,
      data,
      AssessmentResponseSchema
    );
  },

  /**
   * Get assessment by ID.
   */
  async getAssessment(assessmentId: string): Promise<AssessmentResponse> {
    return apiClient.get<AssessmentResponse>(
      `${COMPLIANCE_SERVICE_URL}/assessments/${assessmentId}`,
      AssessmentResponseSchema
    );
  },

  /**
   * List assessments for an application.
   */
  async listAssessments(applicationId?: string, status?: string): Promise<AssessmentListResponse> {
    const params = new URLSearchParams();
    if (applicationId) params.append('application_id', applicationId);
    if (status) params.append('status', status);
    const queryString = params.toString();
    return apiClient.get<AssessmentListResponse>(
      `${COMPLIANCE_SERVICE_URL}/assessments${queryString ? `?${queryString}` : ''}`,
      AssessmentListResponseSchema
    );
  },

  /**
   * Get assessment results.
   */
  async getAssessmentResults(assessmentId: string): Promise<AssessmentResultResponse[]> {
    return apiClient.get<AssessmentResultResponse[]>(
      `${COMPLIANCE_SERVICE_URL}/assessments/${assessmentId}/results`,
      z.array(AssessmentResultResponseSchema)
    );
  },

  /**
   * Poll an assessment until it reaches a terminal state.
   * Terminal states: COMPLETED, FAILED, CANCELLED.
   *
   * @param assessmentId - Assessment ID to poll
   * @param signal - Optional AbortSignal for cancellation
   * @param intervalMs - Polling interval (default: 3000ms)
   * @param timeoutMs - Timeout (default: 300s)
   */
  async pollAssessmentUntilComplete(
    assessmentId: string,
    signal?: AbortSignal,
    intervalMs = 3000,
    timeoutMs = 300_000,
  ): Promise<AssessmentResponse> {
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      if (signal?.aborted) throw new DOMException('Assessment polling aborted', 'AbortError');
      const assessment = await this.getAssessment(assessmentId);
      if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(assessment.status)) return assessment;
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, intervalMs);
        signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
      });
    }
    return this.getAssessment(assessmentId);
  },

  // -------------------------------------------------------------------------
  // Controls
  // -------------------------------------------------------------------------

  /**
   * List control library entries.
   */
  async listControls(framework?: string): Promise<ControlListResponse> {
    const params = framework ? `?framework=${framework}` : '';
    return apiClient.get<ControlListResponse>(
      `${COMPLIANCE_SERVICE_URL}/controls${params}`,
      ControlListResponseSchema
    );
  },

  // -------------------------------------------------------------------------
  // Attestations
  // -------------------------------------------------------------------------

  /**
   * List organization attestations.
   */
  async listOrgAttestations(): Promise<OrganizationAttestationResponse[]> {
    return apiClient.get<OrganizationAttestationResponse[]>(
      `${COMPLIANCE_SERVICE_URL}/attestations/organization`,
      z.array(OrganizationAttestationResponseSchema)
    );
  },

  /**
   * Create organization attestation.
   */
  async createOrgAttestation(data: {
    control_id: string;
    policy_name: string;
    statement: string;
    evidence_description?: string;
    evidence_url?: string;
    valid_until: string;  // ISO date string
  }): Promise<OrganizationAttestationResponse> {
    return apiClient.post<OrganizationAttestationResponse>(
      `${COMPLIANCE_SERVICE_URL}/attestations/organization`,
      data,
      OrganizationAttestationResponseSchema
    );
  },
};

// =============================================================================
// Invitation Types & API
// =============================================================================

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  tenant_id: string;
  invited_by: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface PlatformInvitation extends Invitation {
  scope: 'tenant' | 'platform' | 'demouser';
}

export interface PendingInvitationCheck {
  has_pending: boolean;
  invitation?: Invitation;
}

export interface InvitationAcceptResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  tenant_id: string;
  role: string;
}

export const invitationsApi = {
  async create(data: { email: string; role: string }): Promise<Invitation> {
    return apiClient.post<Invitation>(
      `${DATA_SERVICE_URL}/invitations`,
      data,
      z.any()
    );
  },

  async list(): Promise<Invitation[]> {
    return apiClient.get<Invitation[]>(
      `${DATA_SERVICE_URL}/invitations`,
      z.array(z.any())
    );
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${DATA_SERVICE_URL}/invitations/${id}`);
  },

  async checkPending(): Promise<PendingInvitationCheck> {
    return apiClient.get<PendingInvitationCheck>(
      `${DATA_SERVICE_URL}/invitations/pending`,
      z.any()
    );
  },

  async accept(): Promise<InvitationAcceptResponse> {
    return apiClient.post<InvitationAcceptResponse>(
      `${DATA_SERVICE_URL}/invitations/accept`,
      {},
      z.any()
    );
  },
};

// =============================================================================
// Tenant API
// =============================================================================

export interface TenantInfo {
  id: string;
  name: string;
  created_at: string;
}

export interface PlatformTenantInfo {
  id: string;
  name: string;
  created_by?: string | null;
  is_active?: boolean;
  subscription_status?: string | null;
  subscription_plan_name?: string | null;
  subscription_plan_tier?: string | null;
  subscription_plan_id?: string | null;
  credits_balance?: number | null;
  credits_included?: number | null;
  billing_period_end?: string | null;
  monthly_credits?: number | null;
  created_at: string;
}

export interface PlatformTenantUpdate {
  name?: string;
  is_active?: boolean;
}

export type TrialExtensionWeeks = 2 | 4 | 8;

export interface TenantInvitationFailure {
  email: string;
  reason: string;
}

export interface TenantInvitationBulkResponse {
  invited: Invitation[];
  failed: TenantInvitationFailure[];
}

export interface PlatformUserInfo {
  id: string;
  email: string;
  name: string;
  role: string;
  scope: 'tenant' | 'platform' | 'demouser';
  avatar?: string | null;
  tenant_id: string;
  tenant_name?: string | null;
  is_active: boolean;
  created_at: string;
}

export interface PlatformUserUpdate {
  name?: string;
  role?: 'ADMIN' | 'EDITOR' | 'VIEWER';
  scope?: 'tenant' | 'platform' | 'demouser';
  avatar?: string | null;
  is_active?: boolean;
}

export interface PlatformApplicationInfo {
  id: string;
  name: string;
  description?: string | null;
  tenant_id: string;
  created_at: string;
  scans_count: number;
  behavior_runs_count: number;
  red_team_runs_count: number;
  credits_used: number;
}

export interface PlatformTenantApplicationsInfo {
  credits_balance: number;
  applications: PlatformApplicationInfo[];
}

export const tenantApi = {
  async get(): Promise<TenantInfo> {
    return apiClient.get<TenantInfo>(
      `${DATA_SERVICE_URL}/tenant`,
      z.any()
    );
  },

  async rename(name: string): Promise<TenantInfo> {
    return apiClient.patch<TenantInfo>(
      `${DATA_SERVICE_URL}/tenant`,
      { name },
      z.any()
    );
  },
};

export const platformAdminApi = {
  async createTenant(payload: { name: string }): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants`,
      payload,
      z.any()
    );
  },

  async listTenants(): Promise<PlatformTenantInfo[]> {
    return apiClient.get<PlatformTenantInfo[]>(
      `${DATA_SERVICE_URL}/admin/tenants`,
      z.array(z.any())
    );
  },

  async updateTenant(tenantId: string, payload: PlatformTenantUpdate): Promise<PlatformTenantInfo> {
    return apiClient.patch<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}`,
      payload,
      z.any()
    );
  },

  async extendTenantTrial(tenantId: string, weeks: TrialExtensionWeeks): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/trial/extend`,
      { weeks },
      z.any()
    );
  },

  async grantTenantCredits(tenantId: string, credits: number, reason: string): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/credits/grant`,
      { credits, reason },
      z.any()
    );
  },

  async activateTenantPlan(tenantId: string): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/license/activate`,
      {},
      z.any()
    );
  },

  async setTenantPlanTrial(tenantId: string): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/license/trial`,
      {},
      z.any()
    );
  },

  async listSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    return apiClient.get<SubscriptionPlan[]>(
      `${DATA_SERVICE_URL}/admin/plans`,
      z.array(z.any())
    );
  },

  async changeTenantPlan(tenantId: string, planId: string): Promise<PlatformTenantInfo> {
    return apiClient.post<PlatformTenantInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/license/change-plan`,
      { plan_id: planId },
      z.any()
    );
  },

  async deleteTenant(tenantId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}`,
      z.any()
    );
  },

  async listUsersByTenant(tenantId: string): Promise<PlatformUserInfo[]> {
    return apiClient.get<PlatformUserInfo[]>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/users`,
      z.array(z.any())
    );
  },

  async listApplicationsByTenant(tenantId: string): Promise<PlatformTenantApplicationsInfo> {
    return apiClient.get<PlatformTenantApplicationsInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/applications`,
      z.any()
    );
  },

  async deleteUser(tenantId: string, userId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/users/${userId}`,
      z.any()
    );
  },

  async updateUser(
    tenantId: string,
    userId: string,
    payload: PlatformUserUpdate
  ): Promise<PlatformUserInfo> {
    return apiClient.patch<PlatformUserInfo>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/users/${userId}`,
      payload,
      z.any()
    );
  },

  async inviteUsersToTenant(
    tenantId: string,
    payload: { emails: string[]; role: 'ADMIN' | 'EDITOR' | 'VIEWER'; scope?: 'tenant' | 'demouser' }
  ): Promise<TenantInvitationBulkResponse> {
    return apiClient.post<TenantInvitationBulkResponse>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/invitations`,
      payload,
      z.any()
    );
  },

  async listInvitationsByTenant(tenantId: string): Promise<PlatformInvitation[]> {
    return apiClient.get<PlatformInvitation[]>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/invitations`,
      z.array(z.any())
    );
  },

  async revokeInvitationByTenant(tenantId: string, invitationId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/invitations/${invitationId}`,
      z.any()
    );
  },

  async resendInvitationByTenant(tenantId: string, invitationId: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(
      `${DATA_SERVICE_URL}/admin/tenants/${tenantId}/invitations/${invitationId}/resend`,
      {},
      z.any()
    );
  },
};

// =============================================================================
// ServiceNow Integrations API (data_service — /api/data/integrations)
// =============================================================================

import type { ServiceNowIntegration } from '../types';

/** Shape of the raw API response from the backend. */
interface _SnApiResponse {
  id: string;
  tenant_id: string;
  provider: string;
  enabled: boolean;
  instance_url: string | null;
  user_id: string | null;
  client_id: string | null;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

/** One probe step from a ServiceNow connection test. */
export interface SNTestStepResult {
  step: string;
  status: 'success' | 'failure';
  httpStatusCode: number | null;
  message: string;
  recordsReturned?: number | null;
}

/** Full result from POST /api/data/integrations/servicenow/test. */
export interface SNTestConnectionResult {
  overallStatus: 'success' | 'partial_failure' | 'failure';
  getProbe: SNTestStepResult;
  patchProbe: SNTestStepResult;
}

function _mapSn(r: _SnApiResponse): ServiceNowIntegration {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    provider: r.provider,
    enabled: r.enabled,
    instanceUrl: r.instance_url ?? null,
    userId: r.user_id ?? null,
    clientId: r.client_id ?? null,
    hasCredentials: r.has_credentials,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const snIntegrationsApi = {
  /** Fetch ServiceNow integration config for the current tenant. Always returns a value — the backend auto-creates a disabled default row if none exists. */
  async getServiceNow(): Promise<ServiceNowIntegration | null> {
    const raw = await apiClient.get<_SnApiResponse>(
      `${DATA_SERVICE_URL}/integrations/servicenow`,
      z.any()
    );
    return _mapSn(raw);
  },

  /** Toggle the enabled flag. */
  async updateEnabled(enabled: boolean): Promise<ServiceNowIntegration> {
    const raw = await apiClient.patch<_SnApiResponse>(
      `${DATA_SERVICE_URL}/integrations/servicenow/enabled`,
      { enabled },
      z.any()
    );
    return _mapSn(raw);
  },

  /**
   * Save all five ServiceNow connection fields atomically.
   * Sensitive values (password, client_secret) are stored server-side and
   * NEVER echoed back. The response will have `hasCredentials: true` to confirm
   * storage.
   */
  async updateCredentials(
    instanceUrl: string,
    userId: string,
    password: string,
    clientId: string,
    clientSecret: string,
  ): Promise<ServiceNowIntegration> {
    const raw = await apiClient.patch<_SnApiResponse>(
      `${DATA_SERVICE_URL}/integrations/servicenow/credentials`,
      {
        instance_url: instanceUrl,
        user_id: userId,
        password,
        client_id: clientId,
        client_secret: clientSecret,
      },
      z.any()
    );
    return _mapSn(raw);
  },

  /**
   * Test the ServiceNow connection using credentials stored in the DB.
   * Calls POST /api/data/integrations/servicenow/test on the data_service, which
   * reads instance_url and oauth_token from tenant_integrations and proxies the
   * two-step connectivity probe to sn_integration_service.
   * Credentials are never sent to the browser.
   */
  async testConnection(): Promise<SNTestConnectionResult> {
    const raw = await apiClient.post<any>(
      `${DATA_SERVICE_URL}/integrations/servicenow/test`,
      {},
      z.any()
    );
    // Map snake_case fields from backend to camelCase
    return {
      overallStatus: raw.overall_status,
      getProbe: {
        step: raw.test_sn_get_connection.step,
        status: raw.test_sn_get_connection.status,
        httpStatusCode: raw.test_sn_get_connection.http_status_code ?? null,
        message: raw.test_sn_get_connection.message,
        recordsReturned: raw.test_sn_get_connection.records_returned ?? null,
      },
      patchProbe: {
        step: raw.test_sn_patch_connection.step,
        status: raw.test_sn_patch_connection.status,
        httpStatusCode: raw.test_sn_patch_connection.http_status_code ?? null,
        message: raw.test_sn_patch_connection.message,
        recordsReturned: raw.test_sn_patch_connection.records_returned ?? null,
      },
    };
  },

  /**
   * Export AIBOM assets for a scan to ServiceNow.
   *
   * Credentials are never sent from the browser — the data_service reads them
   * from tenant_integrations and proxies the request securely.
   *
   * May take 30–60 seconds for large inventories; uses a 120s nginx timeout.
   */
  async exportAiBom(scanId: string): Promise<SNExportAiBomResult> {
    const raw = await apiClient.post<any>(
      `${DATA_SERVICE_URL}/integrations/servicenow/export-aibom`,
      { scan_id: scanId },
      z.any()
    );
    const mapAsset = (a: any): SNExportAssetResult => ({
      nodeType: a.node_type,
      canonicalId: a.canonical_id,
      uniqueId: a.unique_id,
      sysId: a.sys_id ?? null,
      action: a.action,
      error: a.error ?? null,
    });
    return {
      applicationName: raw.application_name,
      prompts:    (raw.prompts    ?? []).map(mapAsset),
      datasets:   (raw.datasets   ?? []).map(mapAsset),
      models:     (raw.models     ?? []).map(mapAsset),
      agents:     (raw.agents     ?? []).map(mapAsset),
      guardrails: (raw.guardrails ?? []).map(mapAsset),
      tools:      (raw.tools      ?? []).map(mapAsset),
      system: raw.system ? {
        uniqueId: raw.system.unique_id,
        sysId: raw.system.sys_id ?? null,
        action: raw.system.action,
        error: raw.system.error ?? null,
      } : null,
      summary: {
        totalNodes: raw.summary.total_nodes,
        created:    raw.summary.created,
        found:      raw.summary.found,
        errors:     raw.summary.errors,
      },
    };
  },
};

// =============================================================================
// GitHub Integration API (data_service — /api/data/integrations/github)
// =============================================================================

import type { GithubIntegration, AwsIntegration } from '../types';

interface _GithubApiResponse {
  id: string;
  tenant_id: string;
  provider: string;
  enabled: boolean;
  has_token: boolean;
  created_at: string;
  updated_at: string;
}

function _mapGithub(r: _GithubApiResponse): GithubIntegration {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    provider: r.provider,
    enabled: r.enabled,
    hasToken: r.has_token,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const githubIntegrationsApi = {
  /** Fetch the tenant-wide GitHub integration config. */
  async getConfig(): Promise<GithubIntegration> {
    const raw = await apiClient.get<_GithubApiResponse>(
      `${DATA_SERVICE_URL}/integrations/github`,
      z.any()
    );
    return _mapGithub(raw);
  },

  /** Toggle the enabled flag. */
  async updateEnabled(enabled: boolean): Promise<GithubIntegration> {
    const raw = await apiClient.patch<_GithubApiResponse>(
      `${DATA_SERVICE_URL}/integrations/github/enabled`,
      { enabled },
      z.any()
    );
    return _mapGithub(raw);
  },

  /** Save the tenant-wide GitHub token. Never echoed back — response has hasToken: true. */
  async updateToken(token: string): Promise<GithubIntegration> {
    const raw = await apiClient.patch<_GithubApiResponse>(
      `${DATA_SERVICE_URL}/integrations/github/token`,
      { token },
      z.any()
    );
    return _mapGithub(raw);
  },
};

// =============================================================================
// AWS Security Hub Integration API (data_service — /api/data/integrations/aws)
// =============================================================================

interface _AwsApiResponse {
  id: string;
  tenant_id: string;
  provider: string;
  enabled: boolean;
  region: string | null;
  aws_account_id: string | null;
  profile: string | null;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

function _mapAws(r: _AwsApiResponse): AwsIntegration {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    provider: r.provider,
    enabled: r.enabled,
    region: r.region,
    awsAccountId: r.aws_account_id,
    profile: r.profile,
    hasCredentials: r.has_credentials,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const awsIntegrationsApi = {
  /** Fetch the tenant-wide AWS Security Hub integration config. */
  async getConfig(): Promise<AwsIntegration> {
    const raw = await apiClient.get<_AwsApiResponse>(
      `${DATA_SERVICE_URL}/integrations/aws`,
      z.any()
    );
    return _mapAws(raw);
  },

  /** Toggle the enabled flag. */
  async updateEnabled(enabled: boolean): Promise<AwsIntegration> {
    const raw = await apiClient.patch<_AwsApiResponse>(
      `${DATA_SERVICE_URL}/integrations/aws/enabled`,
      { enabled },
      z.any()
    );
    return _mapAws(raw);
  },

  /**
   * Save region/account ID/profile + optional access/secret keys atomically.
   * Blank access_key_id/secret_access_key mean "keep existing" (same
   * convention as ServiceNow credentials). Leave both blank on first save to
   * rely on ambient AWS credentials (env vars / instance role) instead.
   */
  async updateConfig(
    region: string,
    awsAccountId: string,
    profile: string = '',
    accessKeyId: string = '',
    secretAccessKey: string = '',
  ): Promise<AwsIntegration> {
    const raw = await apiClient.patch<_AwsApiResponse>(
      `${DATA_SERVICE_URL}/integrations/aws/config`,
      {
        region,
        aws_account_id: awsAccountId,
        profile,
        access_key_id: accessKeyId,
        secret_access_key: secretAccessKey,
      },
      z.any()
    );
    return _mapAws(raw);
  },
};

// =============================================================================
// ServiceNow AIBOM Export Types
// =============================================================================

export interface SNExportAssetResult {
  nodeType: string;
  canonicalId: string;
  uniqueId: string;
  sysId: string | null;
  action: 'created' | 'found' | 'error';
  error: string | null;
}

export interface SNExportSystemResult {
  uniqueId: string;
  sysId: string | null;
  action: 'created' | 'found' | 'error';
  error: string | null;
}

export interface SNExportSummary {
  totalNodes: number;
  created: number;
  found: number;
  errors: number;
}

export interface SNExportAiBomResult {
  applicationName: string;
  prompts: SNExportAssetResult[];
  datasets: SNExportAssetResult[];
  models: SNExportAssetResult[];
  agents: SNExportAssetResult[];
  guardrails: SNExportAssetResult[];
  tools: SNExportAssetResult[];
  system: SNExportSystemResult | null;
  summary: SNExportSummary;
}

// =============================================================================
// Chat API  (ai_chatbot_service — /api/chat)
// =============================================================================

export interface ChatApiMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ChatApiContext {
  application_id?: string;
  tenant_id?: string;
  scan_id?: string;
}

export interface ChatApiAction {
  action_id?: string;
  type: 'open_scan_details' | 'open_doc_reference' | 'open_redteam_test' | 'run_redteam_test' | 'execute_tool' | 'escalate_to_support';
  label: string;
  path?: string | null;
  params?: {
    tool_name?: string;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
  requires_confirm?: boolean;
  side_effect?: boolean;
  agent_id?: string | null;
  tool_name?: string | null;
  application_id?: string;
  scan_id?: string;
  doc_path?: string;
  test_run_id?: string;
  target_url?: string;
  scan_profile?: 'ci' | 'full';
  scenario_types?: string[];
  chat_path?: string;
}

/**
 * Server-authorized citation for a fact stated in an assistant reply
 * (issue #888). Mirrors backend ai_chatbot_service/schemas.py::Citation —
 * deliberately no snippet/excerpt/body-text field, see that class's
 * docstring for why.
 */
export interface ChatCitation {
  citation_id: string;
  source_type: 'report' | 'application' | 'scan' | 'finding' | 'doc';
  label: string;
  path: string;
}

export interface ChatRunRedTeamRequest {
  application_id: string;
  scan_id?: string;
  target_url: string;
  scan_profile?: 'ci' | 'full';
  scenario_types?: string[];
  chat_path?: string;
}

export interface ChatRunRedTeamResponse {
  test_run_id: string;
  status: string;
  message?: string;
}

export interface ChatToolExecuteRequest {
  tool_name: string;
  args: Record<string, unknown>;
  confirmed?: boolean;
}

export interface ChatToolExecuteResponse {
  tool_name: string;
  ok: boolean;
  data: unknown;
  error?: string;
  result_text: string;
}

/**
 * Options for a single streamed chat turn. With server-side history the client
 * sends only the new user `message` plus the `conversationId` handle — never the
 * whole thread. The server reconstructs prior turns from the database.
 */
export interface ChatSendOptions {
  conversationId?: string | null;
  message: ChatApiMessage;
  context?: ChatApiContext;
  onDelta?: (delta: string) => void;
  onActions?: (actions: ChatApiAction[]) => void;
  onReplace?: (content: string) => void;
  onStatus?: (content: string) => void;
  onCitations?: (citations: ChatCitation[]) => void;
  /** Called with the server-side conversation id (from the X-Conversation-Id header). */
  onConversationId?: (conversationId: string) => void;
}

export interface ChatSendResult {
  content: string;
  conversationId: string | null;
}

export interface ChatStoredMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  actions?: ChatApiAction[] | null;
  citations?: ChatCitation[] | null;
  created_at?: string | null;
}

export interface ChatConversationMessages {
  conversation_id: string;
  messages: ChatStoredMessage[];
}

// Runtime schemas for resumed conversation payloads. Tightened from z.any() so
// malformed / contract-drifted server data fails validation (→ resume falls back
// to a fresh conversation) instead of surfacing as a runtime error when the panel
// rehydrates. Actions keep unknown keys (passthrough) so optional deep-link params
// (path, params, scan_id, …) survive; citations have a closed, known shape.
const ChatCitationSchema = z.object({
  citation_id: z.string(),
  source_type: z.enum(['report', 'application', 'scan', 'finding', 'doc']),
  label: z.string(),
  path: z.string(),
});

const ChatActionSchema = z.object({
  type: z.enum([
    'open_scan_details',
    'open_doc_reference',
    'open_redteam_test',
    'run_redteam_test',
    'execute_tool',
    'escalate_to_support',
  ]),
  label: z.string(),
}).passthrough();

const chatApi = {
  /**
   * Send a single new user turn to the chat service with SSE streaming.
   * Calls `onDelta` for each token delta as it arrives, and `onConversationId`
   * with the server-side conversation handle. Returns the assembled response
   * plus the conversation id.
   */
  async sendMessage(opts: ChatSendOptions): Promise<ChatSendResult> {
    const { conversationId, message, context, onDelta, onActions, onReplace, onStatus, onCitations, onConversationId } = opts;
    // Use apiClient.streamPost so the request goes through the same auth /
    // token-refresh / 401-retry pipeline as every other service (assets,
    // compliance, etc.).
    const response = await apiClient.streamPost(
      `${CHAT_SERVICE_URL}/chat/stream`,
      {
        message,
        stream: true,
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(context ? { context } : {}),
      },
      CONFIG.ENABLE_CHAT_ORCHESTRATOR ? { 'X-NuGuard-Orchestrator': 'true' } : {}
    );

    // The conversation handle is returned as a response header so the SSE event
    // stream shape is unchanged. Surface it immediately, before parsing tokens.
    const resolvedConversationId = response.headers.get('X-Conversation-Id') || conversationId || null;
    if (resolvedConversationId) onConversationId?.(resolvedConversationId);

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body from chat service');

    const decoder = new TextDecoder();
    let fullContent = '';
    let buffer = '';
    let currentEvent = 'message';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? ''; // Keep incomplete last line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
          currentEvent = 'message';
          continue;
        }
        if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim() || 'message';
          continue;
        }
        if (!line.startsWith('data:')) continue;
        const raw = line.slice(5).trim();
        if (raw === '[DONE]') return { content: fullContent, conversationId: resolvedConversationId };
        try {
          if (currentEvent === 'action') {
            const parsed = JSON.parse(raw) as { actions?: ChatApiAction[] };
            onActions?.(Array.isArray(parsed.actions) ? parsed.actions : []);
          } else if (currentEvent === 'citation') {
            const parsed = JSON.parse(raw) as { citations?: ChatCitation[] };
            onCitations?.(Array.isArray(parsed.citations) ? parsed.citations : []);
          } else if (currentEvent === 'status') {
            const parsed = JSON.parse(raw) as { content?: string };
            onStatus?.(parsed.content ?? '');
          } else if (currentEvent === 'replace') {
            const parsed = JSON.parse(raw) as { content?: string };
            if (parsed.content !== undefined) {
              fullContent = parsed.content;
              onReplace?.(parsed.content);
            }
          } else {
            const parsed = JSON.parse(raw) as { content?: string; error?: string };
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.content) {
              fullContent += parsed.content;
              onDelta?.(parsed.content);
            }
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue; // skip malformed lines
          throw e;
        }
      }
    }

    return { content: fullContent, conversationId: resolvedConversationId };
  },

  /** Explicitly create a server-side conversation (optional; the stream endpoint also lazily creates one). */
  async createConversation(context?: ChatApiContext): Promise<{ conversation_id: string; created_at?: string | null }> {
    return apiClient.post(
      `${CHAT_SERVICE_URL}/chat/conversations`,
      { application_id: context?.application_id, scan_id: context?.scan_id },
      z.object({ conversation_id: z.string(), created_at: z.string().optional().nullable() }),
    );
  },

  /** End (delete) a conversation. Best-effort — safe to ignore failures. Idempotent server-side. */
  async endConversation(conversationId: string, reason: 'user_closed' | 'logout' = 'user_closed'): Promise<void> {
    await apiClient.post(
      `${CHAT_SERVICE_URL}/chat/conversations/${encodeURIComponent(conversationId)}/end`,
      { reason },
      z.object({ conversation_id: z.string(), ended: z.boolean() }),
    );
  },

  /** Rehydrate a conversation's messages for resume-on-reload. Throws (404) if ended/not-found. */
  async getConversationMessages(conversationId: string): Promise<ChatConversationMessages> {
    return apiClient.get<ChatConversationMessages>(
      `${CHAT_SERVICE_URL}/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      z.object({
        conversation_id: z.string(),
        messages: z.array(z.object({
          role: z.enum(['user', 'assistant', 'system']),
          content: z.string(),
          actions: z.array(ChatActionSchema).optional().nullable(),
          citations: z.array(ChatCitationSchema).optional().nullable(),
          created_at: z.string().optional().nullable(),
        })),
      }),
    );
  },

  async runRedteamTest(request: ChatRunRedTeamRequest, actionPath?: string): Promise<ChatRunRedTeamResponse> {
    const responseSchema = z.object({
      test_run_id: z.string(),
      status: z.string(),
      message: z.string().optional(),
    });
    const primaryUrl = `${CHAT_SERVICE_URL}/tests/run-redteam`;

    try {
      return await apiClient.post<ChatRunRedTeamResponse>(primaryUrl, request, responseSchema);
    } catch (error) {
      const fallbackPath = safeChatActionPath(actionPath);
      const fallbackUrl = fallbackPath ? `${CHAT_SERVICE_URL}${fallbackPath}` : null;
      if (!(error instanceof ApiError) || error.status !== 404 || !fallbackUrl || fallbackUrl === primaryUrl) {
        throw error;
      }

      return apiClient.post<ChatRunRedTeamResponse>(fallbackUrl, request, responseSchema);
    }
  },

  async executeTool(request: ChatToolExecuteRequest): Promise<ChatToolExecuteResponse> {
    return apiClient.post<ChatToolExecuteResponse>(
      `${CHAT_SERVICE_URL}/actions/execute`,
      request,
      z.object({
        tool_name: z.string(),
        ok: z.boolean(),
        data: z.unknown(),
        error: z.string().optional().nullable(),
        result_text: z.string(),
      }),
    );
  },

  async escalateToSupport(summary: string): Promise<void> {
    await apiClient.post(
      `${CHAT_SERVICE_URL}/escalate`,
      { summary },
      z.object({ ok: z.boolean() }),
    );
  },
};

// =============================================================================
// Red Teaming API (nuguard)
// =============================================================================

import type {
  ScenarioTypeInfo,
  RunRedTeamRequest,
  TestRun,
  RunBehaviorRequest,
  BehaviorRun,
} from '../types';

const BEHAVIOR_SERVICE_URL = CONFIG.BEHAVIOR_SERVICE_URL || '/api/behavior';

export const redteamApi = {
  _extractFilename(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) return fallback;
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (basicMatch?.[1]) return basicMatch[1];
    return fallback;
  },

  /**
   * List all test runs for current tenant (newest first)
   * GET /api/redteam/tests
   */
async listTestRuns(limit = 20, offset = 0, applicationId?: string, scanId?: string): Promise<{ runs: TestRun[]; total: number }> {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (applicationId) params.append('application_id', applicationId);
      if (scanId) params.append('scan_id', scanId);
      return apiClient.get<{ runs: TestRun[]; total: number }>(
        `${REDTEAM_SERVICE_URL}/tests?${params}`,
        z.object({
          runs: z.array(z.any()),
          total: z.number(),
      })
    );
  },

  /**
   * Run a new red team test
   * POST /api/redteam/run
   */
  async runRedteam(request: RunRedTeamRequest): Promise<{ test_run_id: string; status: string }> {
    return apiClient.post<{ test_run_id: string; status: string }>(
      `${REDTEAM_SERVICE_URL}/run`,
      request,
      z.object({
        test_run_id: z.string(),
        status: z.string(),
        message: z.string().optional()
      })
    );
  },

  /**
   * Get test run status and results
   * GET /api/redteam/test/{test_run_id}
   */
async getTestStatus(testRunId: string): Promise<TestRun> {
      const cacheBust = Date.now();
      return apiClient.get<TestRun>(
        `${REDTEAM_SERVICE_URL}/test/${testRunId}?_=${cacheBust}`,
        z.any()
    );
  },

/**
     * Export full analysis result as JSON.
     * GET /api/redteam/test/{test_run_id}/export
     */
    async exportTestResults(testRunId: string): Promise<{ blob: Blob; filename: string }> {
      const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      const response = await fetch(`${REDTEAM_SERVICE_URL}/test/${testRunId}/export`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const fallback = `redteam_export_${testRunId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.json`;
      const filename = redteamApi._extractFilename(response.headers.get('content-disposition'), fallback);
      return { blob, filename };
    },

    /**
     * Export Markdown report.
     * GET /api/redteam/test/{test_run_id}/export/markdown
     */
    async exportTestResultsMarkdown(testRunId: string): Promise<{ blob: Blob; filename: string }> {
      const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      const response = await fetch(`${REDTEAM_SERVICE_URL}/test/${testRunId}/export/markdown`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const fallback = `redteam_report_${testRunId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.md`;
      const filename = redteamApi._extractFilename(response.headers.get('content-disposition'), fallback);
      return { blob, filename };
  },

  /**
   * Delete a red team test run and associated results
   * DELETE /api/redteam/test/{test_run_id}
   */
  async deleteTestRun(testRunId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `${REDTEAM_SERVICE_URL}/test/${testRunId}`,
      z.object({
        success: z.boolean(),
        message: z.string(),
      })
    );
  },

  /**
   * Get normalised findings for a red-team run.
   * GET /api/redteam/test/{test_run_id}/findings
   */
  async getTestFindings(
    testRunId: string,
    params: { severity?: string; goal_type?: string; limit?: number; offset?: number },
  ): Promise<{ findings: unknown[]; total: number }> {
    const p = new URLSearchParams({
      limit: String(params.limit ?? 20),
      offset: String(params.offset ?? 0),
    });
    if (params.severity) p.append('severity', params.severity);
    if (params.goal_type) p.append('goal_type', params.goal_type);
    return apiClient.get(`${REDTEAM_SERVICE_URL}/test/${testRunId}/findings?${p}`);
  },

  /**
   * Get consolidated remediation artefacts for a red-team run.
   * GET /api/redteam/test/{test_run_id}/remediation
   */
  async getTestRunRemediation(
    testRunId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<{ artefacts: unknown[]; total: number }> {
    const p = new URLSearchParams({
      limit: String(params.limit ?? 100),
      offset: String(params.offset ?? 0),
    });
    return apiClient.get(`${REDTEAM_SERVICE_URL}/test/${testRunId}/remediation?${p}`);
  },

  /**
     * List available adversarial scenario types.
     * GET /api/redteam/scenarios
     */
    async listScenarios(): Promise<{ scenarios: ScenarioTypeInfo[] }> {
      const data = await apiClient.get<any>(`${REDTEAM_SERVICE_URL}/scenarios`);
      const items: ScenarioTypeInfo[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.scenarios)
          ? data.scenarios
          : [];
      return { scenarios: items };
    },
};

// =============================================================================
// Behavior Testing API (nuguard)
// =============================================================================

export const behaviorApi = {
  _extractFilename(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) return fallback;
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (basicMatch?.[1]) return basicMatch[1];
    return fallback;
  },

  /**
   * Run nuguard target verify probe against a URL + endpoint.
   * POST /api/behavior/target-verify
   */
  async verifyTarget(params: {
    target_url: string;
    chat_path?: string;
    auth_type: string;
    /** Token for bearer/api_key auth */
    auth_value?: string;
    /** Username for basic auth */
    auth_username?: string;
    /** Password for basic auth */
    auth_password?: string;
    /** Explicit login endpoint for login_flow auth bootstrap */
    login_path?: string;
    /** Extra static JSON fields merged into every chat POST body (e.g. {"user_id": "alice"}) */
    chat_payload_extras?: Record<string, unknown>;
    /** When provided, the discovered chat path is persisted to the AIBOMScan record */
    scan_id?: string;
  }): Promise<{
    all_ok: boolean;
    endpoint: string;
    discovered_endpoint?: string | null;
    checks: Array<{
      identity: string;
      status: string;
      http_status_code?: number;
      response_time_ms?: number;
      endpoint: string;
      error_detail?: string;
    }>;
    /** Best-effort profile (customer name / account IDs) from nuguard's live discovery conversation */
    discovered_profile?: {
      customer_name: string;
      ids: string[];
      entity_map?: Record<string, string>;
      raw_response?: string;
      source?: string;
    } | null;
    /** Suggestion to configure chat_payload_extras when discovery came back empty */
    discovery_hint?: { message: string; suggested_fields: string[] } | null;
  }> {
    return apiClient.post(
      `${BEHAVIOR_SERVICE_URL}/target-verify`,
      params,
      z.object({
        all_ok: z.boolean(),
        endpoint: z.string(),
        discovered_endpoint: z.string().optional().nullable(),
        checks: z.array(z.object({
          identity: z.string(),
          status: z.string(),
          http_status_code: z.number().optional().nullable(),
          response_time_ms: z.number().optional().nullable(),
          endpoint: z.string(),
          error_detail: z.string().optional().nullable(),
        })),
        discovered_profile: z.object({
          customer_name: z.string(),
          ids: z.array(z.string()),
          entity_map: z.record(z.string()).optional(),
          raw_response: z.string().optional(),
          source: z.string().optional(),
        }).optional().nullable(),
        discovery_hint: z.object({
          message: z.string(),
          suggested_fields: z.array(z.string()),
        }).optional().nullable(),
      })
    );
  },

  /**
   * List all behavior runs for current tenant (newest first)
   * GET /api/behavior/tests
   */
  async listRuns(limit = 20, offset = 0, applicationId?: string, scanId?: string): Promise<{ runs: BehaviorRun[]; total: number }> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (applicationId) params.append('application_id', applicationId);
    if (scanId) params.append('scan_id', scanId);
    return apiClient.get<{ runs: BehaviorRun[]; total: number }>(
      `${BEHAVIOR_SERVICE_URL}/tests?${params}`,
      z.object({
        runs: z.array(z.any()),
        total: z.number(),
      })
    );
  },

  /**
   * Start a new behavior analysis run
   * POST /api/behavior/run
   */
  async runBehavior(request: RunBehaviorRequest): Promise<{ test_run_id: string; status: string }> {
    return apiClient.post<{ test_run_id: string; status: string }>(
      `${BEHAVIOR_SERVICE_URL}/run`,
      request,
      z.object({
        test_run_id: z.string(),
        status: z.string(),
        message: z.string().optional(),
      })
    );
  },

  /**
   * Get behavior run status and results
   * GET /api/behavior/test/{run_id}
   */
  async getRunStatus(runId: string): Promise<BehaviorRun> {
    const cacheBust = Date.now();
    return apiClient.get<BehaviorRun>(
      `${BEHAVIOR_SERVICE_URL}/test/${runId}?_=${cacheBust}`,
      z.any()
    );
  },

  /**
   * Export full analysis result as JSON.
   * GET /api/behavior/test/{run_id}/export
   */
  async exportRunResults(runId: string): Promise<{ blob: Blob; filename: string }> {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const response = await fetch(`${BEHAVIOR_SERVICE_URL}/test/${runId}/export`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
    const blob = await response.blob();
    const fallback = `behavior_export_${runId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.json`;
    const filename = behaviorApi._extractFilename(response.headers.get('content-disposition'), fallback);
    return { blob, filename };
  },

  /**
   * Export Markdown report.
   * GET /api/behavior/test/{run_id}/export/markdown
   */
  async exportRunMarkdown(runId: string): Promise<{ blob: Blob; filename: string }> {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const response = await fetch(`${BEHAVIOR_SERVICE_URL}/test/${runId}/export/markdown`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
    const blob = await response.blob();
    const fallback = `behavior_report_${runId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.md`;
    const filename = behaviorApi._extractFilename(response.headers.get('content-disposition'), fallback);
    return { blob, filename };
  },

  /**
   * Delete a behavior run and associated results
   * DELETE /api/behavior/test/{run_id}
   */
  async deleteRun(runId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `${BEHAVIOR_SERVICE_URL}/test/${runId}`,
      z.object({
        success: z.boolean(),
        message: z.string(),
      })
    );
  },

  /**
   * Get normalised findings for a behavior run.
   * GET /api/behavior/test/{run_id}/findings
   */
  async getRunFindings(
    runId: string,
    params: { severity?: string; goal_type?: string; limit?: number; offset?: number },
  ): Promise<{ findings: unknown[]; total: number }> {
    const p = new URLSearchParams({
      limit: String(params.limit ?? 20),
      offset: String(params.offset ?? 0),
    });
    if (params.severity) p.append('severity', params.severity);
    if (params.goal_type) p.append('goal_type', params.goal_type);
    return apiClient.get(`${BEHAVIOR_SERVICE_URL}/test/${runId}/findings?${p}`);
  },

  /**
   * Get consolidated remediation artefacts for a behavior run.
   * GET /api/behavior/test/{run_id}/remediation
   */
  async getRunRemediation(
    runId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<{ artefacts: unknown[]; total: number }> {
    const p = new URLSearchParams({
      limit: String(params.limit ?? 100),
      offset: String(params.offset ?? 0),
    });
    return apiClient.get(`${BEHAVIOR_SERVICE_URL}/test/${runId}/remediation?${p}`);
  },

  /**
   * List available behavior scenario types.
   * GET /api/behavior/scenarios
   */
  async listScenarios(): Promise<{ scenarios: ScenarioTypeInfo[] }> {
    const data = await apiClient.get<any>(`${BEHAVIOR_SERVICE_URL}/scenarios`);
    const items: ScenarioTypeInfo[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.scenarios)
        ? data.scenarios
        : [];
    return { scenarios: items };
  },
};

// =============================================================================
// NuGuard SBOM plugin analysis API (vuln / deps / atlas / reanalyze)
// =============================================================================
import {
  VulnScanResultSchema,
  DepAnalysisResultSchema,
  AtlasMapResultSchema,
  NgaRulesResultSchema,
  DeepScanResultSchema,
  CognitivePolicyBehaviorResultSchema,
  StaticAnalysisRunResultSchema,
  type VulnScanResult,
  type DepAnalysisResult,
  type AtlasMapResult,
  type NgaRulesResult,
  type DeepScanResult,
  type CognitivePolicyBehaviorResult,
  type StaticAnalysisRunResult,
} from '../schemas';

export const analyzeApi = {
  /** Vulnerability scan results for a scan (cached, lazy-computed). */
  async getVuln(scanId: string): Promise<VulnScanResult> {
    return apiClient.get<VulnScanResult>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/vuln`,
      VulnScanResultSchema,
    );
  },

  /** Dependency analysis results with OSV CVE data (cached, lazy-computed). */
  async getDeps(scanId: string): Promise<DepAnalysisResult> {
    return apiClient.get<DepAnalysisResult>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/deps`,
      DepAnalysisResultSchema,
    );
  },

  /** MITRE Atlas technique mappings (cached, lazy-computed). */
  async getAtlas(scanId: string): Promise<AtlasMapResult> {
    return apiClient.get<AtlasMapResult>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/atlas`,
      AtlasMapResultSchema,
    );
  },

  /** Force re-run all NuGuard SBOM analysis plugins (clears cache). EDITOR+ required. */
  async reanalyze(scanId: string): Promise<unknown> {
    return apiClient.post<unknown>(
      `${ASSETS_SERVICE_URL}/scans/${scanId}/reanalyze`,
      {},
      z.any(),
    );
  },

  /** NGA rules static analysis results (cached, lazy-computed). */
  async getNgaRules(scanId: string): Promise<NgaRulesResult> {
    return apiClient.get<NgaRulesResult>(
      `${ASSETS_SERVICE_URL}/scans/${encodeURIComponent(scanId)}/nga-rules`,
      NgaRulesResultSchema,
    );
  },

  /** NGA-SC supply chain rules analysis results (cached, lazy-computed). */
  async getNgaScRules(scanId: string): Promise<NgaRulesResult> {
    return apiClient.get<NgaRulesResult>(
      `${ASSETS_SERVICE_URL}/scans/${encodeURIComponent(scanId)}/nga-sc-rules`,
      NgaRulesResultSchema,
    );
  },

  /** Deep Scan (checkov/semgrep/trivy) static analysis results (cached, lazy-computed). */
  async getDeepScan(scanId: string): Promise<DeepScanResult> {
    return apiClient.get<DeepScanResult>(
      `${ASSETS_SERVICE_URL}/scans/${encodeURIComponent(scanId)}/deep-scan`,
      DeepScanResultSchema,
    );
  },

  /** All 18 NuGuard rule definitions from the NuGuard Rules system policy (no scan required). */
  async getAllNgaRuleDefinitions(): Promise<PolicyControl[]> {
    return apiClient.get<PolicyControl[]>(
      `${COMPLIANCE_SERVICE_URL}/policies/nga-rules/controls`,
      z.array(PolicyControlSchema),
    );
  },

  /** Static cognitive policy behavior analysis results (cached, lazy-computed). */
  async getCognitivePolicyBehavior(scanId: string): Promise<CognitivePolicyBehaviorResult> {
    return apiClient.get<CognitivePolicyBehaviorResult>(
      `${ASSETS_SERVICE_URL}/scans/${encodeURIComponent(scanId)}/cognitive-policy-behavior`,
      CognitivePolicyBehaviorResultSchema,
    );
  },

  /** Force re-run NGA rules + cognitive policy behavior analyses. EDITOR+ required. */
  async runStaticAnalysis(scanId: string): Promise<StaticAnalysisRunResult> {
    return apiClient.post<StaticAnalysisRunResult>(
      `${ASSETS_SERVICE_URL}/scans/${encodeURIComponent(scanId)}/run-static-analysis`,
      {},
      StaticAnalysisRunResultSchema,
    );
  },
};

const INTEGRATION_SERVICE_URL = CONFIG.INTEGRATION_SERVICE_URL || '/api/integrations';

export const integrationsApi = {
  /** Export scan as SARIF 2.1.0 JSON */
  async exportSarif(scanId: string): Promise<unknown> {
    return apiClient.post<unknown>(
      `${INTEGRATION_SERVICE_URL}/exports/sarif/${scanId}`,
      {},
      z.any(),
    );
  },

  /** Export scan as CycloneDX SBOM JSON */
  async exportCycloneDx(scanId: string): Promise<unknown> {
    return apiClient.post<unknown>(
      `${INTEGRATION_SERVICE_URL}/exports/cyclonedx/${scanId}`,
      {},
      z.any(),
    );
  },

  /** Export scan as Markdown report */
  async exportMarkdown(scanId: string): Promise<unknown> {
    return apiClient.post<unknown>(
      `${INTEGRATION_SERVICE_URL}/exports/markdown/${scanId}`,
      {},
      z.any(),
    );
  },

  /** Upload SARIF results to GitHub Advanced Security */
  async uploadGhas(
    scanId: string,
    payload?: { repo?: string; ref?: string; commit_sha?: string; token?: string },
  ): Promise<unknown> {
    return apiClient.post<unknown>(
      `${INTEGRATION_SERVICE_URL}/ghas/upload/${scanId}`,
      payload ?? {},
      z.any(),
    );
  },

  /** Push findings to AWS Security Hub */
  async uploadAws(scanId: string): Promise<unknown> {
    return apiClient.post<unknown>(
      `${INTEGRATION_SERVICE_URL}/aws-shub/upload/${scanId}`,
      {},
      z.any(),
    );
  },
};

const LICENSING_SERVICE_URL = CONFIG.LICENSING_SERVICE_URL || '/api/licensing';

export const licensingApi = {
  /** Current subscription plan + credit balance for the authenticated tenant. */
  async getSubscription(): Promise<TenantSubscription> {
    return apiClient.get<TenantSubscription>(
      `${LICENSING_SERVICE_URL}/subscription`,
      TenantSubscriptionSchema,
    );
  },

  /** List active subscription plans (all authenticated users). */
  async getPlans(): Promise<SubscriptionPlan[]> {
    return apiClient.get<SubscriptionPlan[]>(
      `${LICENSING_SERVICE_URL}/plans`,
      z.array(SubscriptionPlanSchema),
    );
  },

  /** Paginated credit transaction history for the tenant. */
  async getTransactions(params?: {
    page?: number;
    page_size?: number;
    start?: string;
    end?: string;
    feature?: string;
  }): Promise<{ items: CreditTransaction[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.feature) qs.set('feature', params.feature);
    const query = qs.toString() ? `?${qs}` : '';
    return apiClient.get<{ items: CreditTransaction[]; total: number }>(
      `${LICENSING_SERVICE_URL}/transactions${query}`,
      z.object({ items: z.array(CreditTransactionSchema), total: z.number() }),
    );
  },

  /** Aggregated usage summary — array of billing-period summaries (MTD + last 3 months). */
  async getUsageSummary(): Promise<UsageSummary[]> {
    return apiClient.get<UsageSummary[]>(
      `${LICENSING_SERVICE_URL}/usage/summary`,
      z.array(UsageSummarySchema),
    );
  },

  /** Per-application usage breakdown (admin only). */
  async getUsageByApplication(): Promise<UsageByDimension[]> {
    return apiClient.get<UsageByDimension[]>(
      `${LICENSING_SERVICE_URL}/usage/by-application`,
      z.array(UsageByDimensionSchema),
    );
  },

  /** Per-user usage breakdown (admin only). */
  async getUsageByUser(): Promise<UsageByDimension[]> {
    return apiClient.get<UsageByDimension[]>(
      `${LICENSING_SERVICE_URL}/usage/by-user`,
      z.array(UsageByDimensionSchema),
    );
  },

  /** Current quota status (balance, warning, PAYG flags). */
  async getQuotaStatus(): Promise<QuotaStatus> {
    return apiClient.get<QuotaStatus>(
      `${LICENSING_SERVICE_URL}/subscription`,
      QuotaStatusSchema,
    );
  },

  /** Active (unresolved, not-yet-dismissed) licensing notifications for the tenant. */
  async getNotifications(): Promise<LicensingNotification[]> {
    return apiClient.get<LicensingNotification[]>(
      `${LICENSING_SERVICE_URL}/notifications`,
      z.array(LicensingNotificationSchema),
    );
  },

  /** Dismiss a notification (only allowed when it is marked dismissible). */
  async dismissNotification(notificationId: string): Promise<LicensingNotification> {
    return apiClient.post<LicensingNotification>(
      `${LICENSING_SERVICE_URL}/notifications/${notificationId}/dismiss`,
      {},
      LicensingNotificationSchema,
    );
  },

  /** Verify a completed Stripe Checkout session and credit the account immediately. */
  async verifyCheckoutSession(sessionId: string): Promise<{ status: string; credits_added: number }> {
    return apiClient.post<{ status: string; credits_added: number }>(
      `${LICENSING_SERVICE_URL}/stripe/verify-session`,
      { session_id: sessionId },
      z.object({ status: z.string(), credits_added: z.number() }),
    );
  },

  /** Create a Stripe Checkout session to purchase AI credits. Redirects to Stripe. */
  async createCheckoutSession(creditsToPurchase: number): Promise<{ checkout_url: string }> {
    return apiClient.post<{ checkout_url: string }>(
      `${LICENSING_SERVICE_URL}/stripe/checkout`,
      {
        credits_to_purchase: creditsToPurchase,
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
      },
      z.object({ checkout_url: z.string() }),
    );
  },

  /** Create a Stripe Checkout session to complete 3DS re-authentication for a pending auto-recharge. */
  async create3dsReauthSession(): Promise<{ checkout_url: string }> {
    return apiClient.post<{ checkout_url: string }>(
      `${LICENSING_SERVICE_URL}/stripe/3ds-reauth`,
      {
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
      },
      z.object({ checkout_url: z.string() }),
    );
  },

  /** Create a Stripe Checkout session for a monthly or annual subscription upgrade. */
  async createSubscriptionCheckout(params: {
    amount_dollars: number;
    billing_interval: 'monthly' | 'annual';
  }): Promise<{ checkout_url: string }> {
    return apiClient.post<{ checkout_url: string }>(
      `${LICENSING_SERVICE_URL}/stripe/subscribe`,
      {
        amount_dollars: params.amount_dollars,
        billing_interval: params.billing_interval,
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
      },
      z.object({ checkout_url: z.string() }),
    );
  },

  /** Create a Stripe Billing Portal session for self-serve subscription management. */
  async createPortalSession(): Promise<{ portal_url: string }> {
    return apiClient.post<{ portal_url: string }>(
      `${LICENSING_SERVICE_URL}/stripe/portal`,
      { return_url: `${window.location.origin}/settings` },
      z.object({ portal_url: z.string() }),
    );
  },
};

// =============================================================================
// Export combined API object
// =============================================================================
export const api = {
  auth: authApi,
  applications: applicationsApi,
  policies: policiesApi,
  scans: scansApi,
  assets: assetsApi,
  users: usersApi,
  auditLogs: auditLogsApi,
  reports: reportsApi,
  exports: exportsApi,
  aibom: aibomApi,
  compliance: complianceApi,
  invitations: invitationsApi,
  tenant: tenantApi,
  platformAdmin: platformAdminApi,
  chat: chatApi,
  redteaming: redteamApi,
  behavior: behaviorApi,
  integrations: snIntegrationsApi,
  githubIntegrations: githubIntegrationsApi,
  awsIntegrations: awsIntegrationsApi,
  licensing: licensingApi,
};

export default api;
